import { useState, useEffect, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useBreakpoint } from '../hooks/useBreakpoint';
import CoinImage from '../components/CoinImage';
import ExchangeLogo from '../components/ExchangeLogo';
import PickerSheet from '../components/PickerSheet';
import InfoBanner from '../components/InfoBanner';
import { Icon, Icons } from '../components/red/Icons';
import { F, R, EASE, DUR, withAlpha, mr } from '../styles/theme';
import { useTheme } from '../context/ThemeContext';
import { COIN_LIST, COIN_MAP, fetchMarketData, getHistoricalPriceAt } from '../utils/coingecko';
import { EXCHANGE_LABELS, EXCHANGES as EX_REGISTRY, findExchange, PROVIDER_LABELS } from '../utils/exchanges';
import { evaluateTransferPair } from '../utils/transferMatcher';
import { INCOME_TYPES, isFiatCoin } from '../utils/transactionTypes';
import { fmt } from '../utils/format';
import { useT } from '../i18n/useT';
import { useCurrency } from '../context/CurrencyContext';

// ═══════════════════════════════════════════════════════════════════════════════
// BUTTONS — neues Design (Hero-Gradient CTA, Glass-Ghost)
// ═══════════════════════════════════════════════════════════════════════════════
// Calm: Primary = INK-Button (T.text-Fläche, T.bg-Schrift), KEIN Rot. Rot gehört
// app-weit allein der Marke + dem „+" (Doktrin „Rot nur Marke"). Press-Squish.
const mkBtnPrimary = (T) => ({
  width: '100%', padding: '16px 18px',
  background: T.text, color: T.bg,
  border: 'none', borderRadius: mr(16),
  fontFamily: F.family, fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em',
  cursor: 'pointer', marginBottom: 10,
  boxShadow: T.cardShadow,
  WebkitTapHighlightColor: 'transparent',
  transition: `transform 120ms ${EASE}`,
});
// Ghost = ruhige Fläche + Hairline (kein Glassmorphism).
const mkBtnGhost = (T) => ({
  width: '100%', padding: '13px',
  background: 'transparent',
  color: T.sub,
  border: `1px solid ${T.line || T.border}`, borderRadius: mr(14),
  fontFamily: F.family, fontSize: 14, fontWeight: 600, cursor: 'pointer',
  marginBottom: 6, WebkitTapHighlightColor: 'transparent',
});
const mkBtnDisabled = (T) => ({
  ...mkBtnPrimary(T),
  background: T.surfaceHigh, boxShadow: 'none', cursor: 'not-allowed',
  color: T.dim, border: `1px solid ${T.border}`,
});

const EXCHANGES = EXCHANGE_LABELS;

// ═══════════════════════════════════════════════════════════════════════════════
// MOTION — Step-Wechsel blendet per Spring ein, Tile-Listen staffeln (wie App-weit,
// vgl. components/red/Motion.js). Respektiert prefers-reduced-motion.
// ═══════════════════════════════════════════════════════════════════════════════
const WIZ_SPRING = 'cubic-bezier(.16,1,.3,1)';
function wizReduced() {
  return typeof matchMedia !== 'undefined'
    && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const WIZ_STAGGER_CSS = `
@keyframes wizRise { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
.wizStagger > * { animation: wizRise .52s ${WIZ_SPRING} backwards; }
.wizStagger > *:nth-child(1){animation-delay:40ms}
.wizStagger > *:nth-child(2){animation-delay:90ms}
.wizStagger > *:nth-child(3){animation-delay:140ms}
.wizStagger > *:nth-child(4){animation-delay:190ms}
.wizStagger > *:nth-child(5){animation-delay:240ms}
.wizStagger > *:nth-child(6){animation-delay:290ms}
.wizStagger > *:nth-child(7){animation-delay:340ms}
.wizStagger > *:nth-child(8){animation-delay:390ms}
@media (prefers-reduced-motion: reduce){ .wizStagger > * { animation: none; } }
.wizPress:active { transform: scale(.985) !important; }
@media (prefers-reduced-motion: reduce){ .wizPress:active { transform: none !important; } }
@keyframes wizSpin { to { transform: rotate(360deg); } }
/* Dezente Hover-Motion (Calm: kein Lift/Scale der Zeile) — das Chevron einer
   Zeile gleitet beim Drüberfahren nach rechts; das führende Icon rückt leicht. */
.wizChev { transition: transform 220ms cubic-bezier(.16,1,.3,1); }
.wizRow:hover .wizChev, .wizRow:focus-visible .wizChev { transform: translateX(3px); }
.wizLeadIco { transition: transform 220ms cubic-bezier(.16,1,.3,1); }
.wizRow:hover .wizLeadIco { transform: translateX(1px); }
/* Aufklapp-Felder (Datum/Börse): Icon nickt beim Hover dezent nach unten. */
.wizDropIco { transition: transform 220ms cubic-bezier(.16,1,.3,1); }
.wizRow:hover .wizDropIco { transform: translateY(2px); }
/* Runder Close-Knopf: das ×-Icon dreht beim Hover eine Viertelumdrehung. */
.wizCloseIco { transition: transform 240ms cubic-bezier(.16,1,.3,1); }
.wizClose:hover .wizCloseIco { transform: rotate(90deg); }
/* Pastell-Chips (Methode/Typ/Quelle): Hover-Motion ohne Transform-Konflikt mit
   dem inline Aktiv-Scale — der Chip hellt leicht auf, das Wasserzeichen driftet/
   wächst sichtbar (Mikro-Motion am Kind, kein Lift der Fläche). */
.wizChip { transition: filter 200ms ease; }
.wizChip:hover { filter: brightness(1.06); }
.wizChipWm { transition: transform 340ms cubic-bezier(.16,1,.3,1); }
.wizChip:hover .wizChipWm { transform: scale(1.1) rotate(-4deg); }
@media (prefers-reduced-motion: reduce){
  .wizRow:hover .wizChev, .wizRow:hover .wizLeadIco, .wizRow:hover .wizDropIco,
  .wizClose:hover .wizCloseIco, .wizChip:hover .wizChipWm { transform: none; }
}`;

// Wrappt jeden Wizard-Step: blendet bei jedem Step-Wechsel (k) frisch ein.
// useLayoutEffect setzt vor dem Paint auf hidden → kein Flash.
function StepMotion({ k, children }) {
  const [shown, setShown] = useState(false);
  useLayoutEffect(() => {
    if (wizReduced()) { setShown(true); return; }
    setShown(false);
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, [k]);
  return (
    <>
      <style>{WIZ_STAGGER_CSS}</style>
      <div style={{
        opacity: shown ? 1 : 0,
        transform: shown ? 'none' : 'translateY(8px)',
        transition: `opacity .42s ${WIZ_SPRING}, transform .42s ${WIZ_SPRING}`,
        willChange: 'opacity, transform',
      }}>
        {children}
      </div>
    </>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// STEP-HEADER — Calm: sentence-case Headline + leiser Sub, optionaler Icon-Slot.
// Kein Caps-Kicker, kein roter Strich, kein Container.
// ═══════════════════════════════════════════════════════════════════════════════
// Vereinheitlichter Overlay-Header (wie BrandModal/Navbar): Kicker (muted) ·
// Display-Headline · roter 46px-Akzent-Strich. Kein Hairline-Eyebrow / keine
// 2px-Text-Unterkante mehr.
// Calm-Header für jeden Step: KEIN Caps-Kicker, KEIN roter 46px-Strich mehr
// (alte „red glass"-Signatur). Nur sentence-case Headline (Hanken, 700) + leiser
// Sub. Optionaler `icon`-Slot (echtes Coin-Logo als Node oder Glyph als String)
// steht links vor der Headline. `eyebrow`/`accent` werden bewusst ignoriert.
function StepHero({ icon, title, sub }) {
  const { T } = useTheme();
  const iconNode = icon == null ? null
    : (typeof icon === 'string'
        ? <span style={{ fontSize: 22, lineHeight: 1, color: T.sub }}>{icon}</span>
        : icon);
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
        {iconNode && <span style={{ display: 'inline-flex', flexShrink: 0 }}>{iconNode}</span>}
        <h2 style={{
          margin: 0,
          fontFamily: F.display, fontSize: 24, fontWeight: 700,
          letterSpacing: '-0.03em', lineHeight: 1.06, color: T.text,
        }}>{title}</h2>
      </div>
      {sub && (
        <div style={{
          marginTop: 10, fontSize: 13, color: T.sub,
          lineHeight: 1.5, fontFamily: F.family,
        }}>{sub}</div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// FORM PRIMITIVES — glassmorphic, größere Touch-Targets (min 48px), Plus-Jakarta
// ═══════════════════════════════════════════════════════════════════════════════
// Calm-Feld = FLACHE Zeile mit Haarlinie oben (wie im Mock) — KEINE Box, kein
// Hintergrund, kein Radius. Die Felder sitzen lückenlos (gap 0) in einer Spalte,
// der Container schließt unten mit einer Haarlinie. Editorial statt Karten.
const mkFieldBase = (T) => ({
  padding: '13px 2px',
  background: 'transparent',
  border: 'none',
  borderTop: `1px solid ${T.line || T.border}`,
  borderRadius: 0,
  minHeight: 54,
  display: 'flex', flexDirection: 'column', justifyContent: 'center',
});
// Spalten-Wrapper für eine Feldgruppe: lückenlose Haarlinien + Abschluss unten.
const fieldGroupStyle = (T) => ({
  display: 'flex', flexDirection: 'column', gap: 0,
  borderBottom: `1px solid ${T.line || T.border}`,
  marginBottom: 20,
});

// Label sentence-case, ruhig — kein Caps, kein Tracking.
const mkFieldLabel = (T) => ({
  fontSize: 11, color: T.sub, marginBottom: 5,
  fontFamily: F.family, fontWeight: 600, letterSpacing: '-0.005em',
});

const mkFieldInput = (T) => ({
  background: 'none', border: 'none', outline: 'none',
  color: T.text, fontFamily: F.family,
  fontSize: 15, fontWeight: 600, width: '100%',
  letterSpacing: '-0.01em',
});

// „↻ Kurs"-Knopf (historischen Preis holen). Hover-Motion = das Refresh-Zeichen
// dreht eine volle Runde (kein Lift/Scale → Calm-konform); beim Laden dauer-Spin.
// Fläche/Schrift wechseln dezent. Geteilt von Details- + Edit-Formular.
function RateButton({ onClick, disabled, fetching }) {
  const { T } = useTheme();
  const { t } = useT();
  const [hover, setHover] = useState(false);
  const active = hover && !disabled;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '5px 10px', borderRadius: mr(10),
        border: `1px solid ${T.line || T.border}`,
        background: active ? (T.surfaceHi || T.itemHover || T.surface) : T.surface,
        color: active ? T.text : T.sub,
        fontFamily: F.family, fontSize: 11, fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        whiteSpace: 'nowrap', flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', gap: 5,
        transition: `background ${DUR.fast}ms ${EASE}, color ${DUR.fast}ms ${EASE}`,
        WebkitTapHighlightColor: 'transparent',
      }}>
      <span style={{
        display: 'inline-block', lineHeight: 1, fontSize: 12,
        transform: (active && !fetching) ? 'rotate(360deg)' : 'rotate(0deg)',
        transition: `transform 600ms ${WIZ_SPRING}`,
        animation: fetching ? 'wizSpin 0.8s linear infinite' : 'none',
      }}>↻</span>
      {t('tw_rate')}
    </button>
  );
}

function FieldRow({ label, type = 'text', value, onChange, placeholder }) {
  const { T } = useTheme();
  // Datum → markeneigener iOS-Kalender statt nativem <input type="date">
  if (type === 'date') return <FieldDate label={label} value={value} onChange={onChange} />;
  return (
    <div style={mkFieldBase(T)}>
      <div style={mkFieldLabel(T)}>{label}</div>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={mkFieldInput(T)}
      />
    </div>
  );
}

function FieldNum({ label, value, onChange, placeholder }) {
  const { T } = useTheme();
  return (
    <div style={mkFieldBase(T)}>
      <div style={mkFieldLabel(T)}>{label}</div>
      <input
        type="text" inputMode="decimal"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={mkFieldInput(T)}
      />
    </div>
  );
}

function FieldSelect({ label, value, onChange, options, withExchangeLogo = false, allowProvider = false, wallet = null }) {
  const { T } = useTheme();
  // Börsen/Wallets → markeneigener Picker mit Fade-Overlay + echten Logos
  if (withExchangeLogo) return <FieldExchange label={label} value={value} onChange={onChange} options={options} allowProvider={allowProvider} wallet={wallet} />;
  return (
    <div style={mkFieldBase(T)}>
      <div style={mkFieldLabel(T)}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {withExchangeLogo && value ? <ExchangeLogo name={value} size={24} /> : null}
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          style={{ ...mkFieldInput(T), appearance: 'none' }}
        >
          {options.map(o => <option key={o} value={o} style={{ background: T.surface }}>{o}</option>)}
        </select>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PICKER-OVERLAY — markeneigenes Fade-Modal (vgl. BrandModal), aber via Portal an
// document.body gerendert. Pflicht: die Felder sitzen in Glass-Containern mit
// backdrop-filter, das einen Containing-Block für position:fixed bildet — ohne
// Portal würde das Vollbild-Overlay darin eingesperrt statt den Screen zu füllen.
// ═══════════════════════════════════════════════════════════════════════════════
function PickerOverlay({ open, onClose, children, maxWidth = 440 }) {
  const { T } = useTheme();
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div
      onClick={onClose}
      data-kb-overlay
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(8,8,11,0.66)',
        backdropFilter: 'blur(10px) saturate(120%)',
        WebkitBackdropFilter: 'blur(10px) saturate(120%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, paddingBottom: 'calc(16px + var(--kbpad))', fontFamily: F.family,
        transition: 'padding-bottom var(--dur-kb) var(--ease-kb)',
        animation: `rPickFade ${DUR.fast}ms ease both`,
      }}
    >
      <style>{`
        @keyframes rPickFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes rPickIn {
          from { transform: translateY(8px) scale(0.965); opacity: 0 }
          to   { transform: translateY(0)   scale(1);     opacity: 1 }
        }
        .rPickCard { animation: rPickIn 250ms cubic-bezier(0.23,1,0.32,1) both; }
        @media (prefers-reduced-motion: reduce) { .rPickCard { animation: rPickFade ${DUR.fast}ms ease both; } }
      `}</style>
      <div
        className="rPickCard"
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative', width: '100%', maxWidth,
          maxHeight: 'min(82vh, 100%)', display: 'flex', flexDirection: 'column',
          background: T.surface, border: `1px solid ${T.line || T.border}`,
          borderRadius: mr(20), boxShadow: T.cardShadowLg, overflow: 'hidden',
        }}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

// ── kleine Inline-Glyphs (kein zusätzlicher Import-Footprint) ──
function PickClose({ T, onClick }) {
  // Dezente Hover-Motion: Fläche füllt sich, Icon dreht eine Viertelumdrehung.
  return (
    <button onClick={onClick} aria-label="Schließen"
      className="wizPress wizClose"
      onMouseEnter={e => { e.currentTarget.style.background = T.itemHover || 'transparent'; e.currentTarget.style.color = T.text; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.sub; }}
      style={{
        width: 30, height: 30, flexShrink: 0, display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center', background: 'transparent',
        color: T.sub, border: `1px solid ${T.line}`, borderRadius: 999,
        cursor: 'pointer', padding: 0, WebkitTapHighlightColor: 'transparent',
        transition: `background ${DUR.fast}ms ease, color ${DUR.fast}ms ease`,
      }}>
      <svg className="wizCloseIco" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M5 5l14 14M19 5L5 19" />
      </svg>
    </button>
  );
}

// Calm-Picker-Header: sentence-case Headline + runder Close. Kein Wordmark,
// kein Caps-Label, keine Trennlinie.
function PickHeader({ T, label, onClose }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
      <span style={{
        flex: 1, fontFamily: F.display, fontSize: 17, fontWeight: 700,
        letterSpacing: '-0.02em', color: T.text,
      }}>{label}</span>
      <PickClose T={T} onClick={onClose} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// EXCHANGE-PICKER — Fade-Modal mit Suche + nach Typ gruppierter Liste, echte Logos
// ═══════════════════════════════════════════════════════════════════════════════
// Auswahl-Schritt 0 für „Exchange/Wallet": direkt auf der Börse ODER über einen
// Zahlungsdienstleister (MoonPay etc. → Ziel-Börse/Wallet).
function SourceRootOverlay({ open, onClose, onDirect, onProvider }) {
  const { T } = useTheme();
  const { t } = useT();
  const ICONS = {
    bank: <path d="M3 21h18M5 21V10l7-4.5 7 4.5v11M9.5 21v-6h5v6" />,
    card: <><rect x="2.5" y="5.5" width="19" height="13" rx="2" /><path d="M2.5 9.5h19" /></>,
  };
  const tile = (iconPath, title, sub, onClick, last) => (
    <button type="button" onClick={onClick}
      className="wizPress wizRow"
      onMouseEnter={ev => { ev.currentTarget.style.background = T.itemHover || 'transparent'; }}
      onMouseLeave={ev => { ev.currentTarget.style.background = 'transparent'; }}
      style={{
        display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left',
        padding: '16px 4px', border: 'none', borderTop: `1px solid ${T.line}`,
        borderBottom: last ? `1px solid ${T.line}` : 'none', borderRadius: 0,
        background: 'transparent', cursor: 'pointer', color: T.text, fontFamily: F.family,
        WebkitTapHighlightColor: 'transparent', transition: `background ${DUR.fast}ms ease`,
      }}>
      <svg className="wizLeadIco" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.sub}
        strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {iconPath}
      </svg>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 15, fontWeight: 700, letterSpacing: '-0.015em' }}>{title}</span>
        <span style={{ display: 'block', fontSize: 12, color: T.sub, marginTop: 2, lineHeight: 1.4 }}>{sub}</span>
      </span>
      <svg className="wizChev" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M9 18l6-6-6-6" />
      </svg>
    </button>
  );
  return (
    <PickerOverlay open={open} onClose={onClose} maxWidth={460}>
      <div style={{ padding: '18px 20px 6px' }}>
        <PickHeader T={T} label={t('tw_src_root_title')} onClose={onClose} />
      </div>
      <div style={{ padding: '0 20px 18px' }}>
        {tile(ICONS.bank, t('tw_src_direct'),   t('tw_src_direct_sub'),   onDirect)}
        {tile(ICONS.card, t('tw_src_provider'), t('tw_src_provider_sub'), onProvider, true)}
      </div>
    </PickerOverlay>
  );
}

// allowProvider=true  → Schritt-0 (direkt vs. Dienstleister); bei Dienstleister
//   wird `onChange(provider, ziel)` mit ZWEI Werten aufgerufen (exchange=Dienst-
//   leister, wallet=Ziel) → Anzeige „MoonPay → Ledger".
// allowProvider=false → unverändertes Verhalten, `onChange(label)` (2. Arg null).
function FieldExchange({ label, value, onChange, options, allowProvider = false, wallet = null }) {
  const { T } = useTheme();
  const [phase, setPhase] = useState('idle'); // idle|root|direct|providers|dest
  const [chosenProvider, setChosenProvider] = useState(null);
  const customOptions = options && options !== EXCHANGE_LABELS ? options : null;

  const resolved = value ? findExchange(value) : null;
  const baseLabel = value
    ? (resolved && resolved.id !== 'other' ? resolved.label : value)
    : '—';
  // Über Dienstleister gekauft → „MoonPay → Ledger" direkt im Feld zeigen.
  const displayLabel = (allowProvider && wallet) ? `${baseLabel} → ${wallet}` : baseLabel;

  const close = () => { setPhase('idle'); setChosenProvider(null); };
  const openField = () => { setChosenProvider(null); setPhase(allowProvider ? 'root' : 'direct'); };
  const pickDirect = (lbl) => { onChange(lbl, null); close(); };
  const pickProvider = (lbl) => { setChosenProvider(lbl); setPhase('dest'); };
  const pickDest = (lbl) => { onChange(chosenProvider, lbl); close(); };

  return (
    <>
      <button type="button" onClick={openField} className="wizRow"
        style={{ ...mkFieldBase(T), cursor: 'pointer', textAlign: 'left', width: '100%' }}>
        <div style={mkFieldLabel(T)}>{label}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {value ? <ExchangeLogo name={value} size={24} /> : null}
          <span style={{ ...mkFieldInput(T), flex: 1 }}>{displayLabel}</span>
          <svg className="wizDropIco" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>
      </button>

      <SourceRootOverlay
        open={phase === 'root'} onClose={close} label={label}
        onDirect={() => setPhase('direct')}
        onProvider={() => setPhase('providers')}
      />
      {/* Direkte Börse / Ziel-Börse-Wallet: volle Registry (gruppiert) */}
      <ExchangePickerModal
        open={phase === 'direct'} onClose={close} value={value}
        onChange={pickDirect} label={label} customOptions={customOptions}
      />
      {/* 1) Dienstleister wählen — navigiert zum Ziel-Schritt statt zu schließen */}
      <ExchangePickerModal
        open={phase === 'providers'} onClose={close} value={null}
        onChange={pickProvider} label={label} customOptions={PROVIDER_LABELS}
        titleKey="tw_pick_provider_title" onBack={() => setPhase('root')}
        closeOnPick={false}
      />
      {/* 2) Ziel-Börse/Wallet wählen */}
      <ExchangePickerModal
        open={phase === 'dest'} onClose={close} value={wallet}
        onChange={pickDest} label={label}
        titleKey="tw_pick_dest_title" onBack={() => setPhase('providers')}
      />
    </>
  );
}

function ExchangePickerModal({ open, onClose, value, onChange, customOptions, titleKey, closeOnPick = true }) {
  const { t } = useT();
  const selId = (!customOptions && value) ? findExchange(value).id : null;

  // Volle (ungefilterte) Sektionen — das PickerSheet filtert/entprellt selbst und
  // bringt den Search-Mode (öffnet ohne Tastatur, Suche pinnt bei Fokus nach oben).
  const sections = useMemo(() => {
    if (customOptions) {
      return [{
        title: null,
        items: customOptions.map(lbl => ({
          key: lbl, label: lbl, current: lbl === value,
          logo: <ExchangeLogo name={lbl} size={30} />,
          onPick: () => onChange(lbl),
        })),
      }];
    }
    const sec = (kind, title) => ({
      title,
      items: EX_REGISTRY.filter(e => e.kind === kind).map(e => ({
        key: e.id, label: e.label, keywords: e.id, current: e.id === selId,
        logo: <ExchangeLogo name={e.label} size={30} />,
        onPick: () => onChange(e.label),
      })),
    });
    return [
      sec('exchange', t('tw_pick_exchanges')),
      sec('hw', t('tw_pick_hw')),
      sec('sw', t('tw_pick_sw')),
      sec('other', t('tw_pick_other')),
    ].filter(g => g.items.length);
  }, [customOptions, value, selId, t, onChange]);

  return (
    <PickerSheet
      open={open} onClose={onClose}
      title={t(titleKey || 'tw_pick_exchange_title')}
      searchPlaceholder={t('tw_pick_search')}
      sections={sections}
      closeOnPick={closeOnPick}
      emptyText={t('tw_pick_empty')}
    />
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DATE-PICKER — iOS-angelehnter Kalender (Monatsnav, rote runde Auswahl, „Heute")
// Wert bleibt ISO 'YYYY-MM-DD' (lokal, kein UTC-Off-by-one).
// ═══════════════════════════════════════════════════════════════════════════════
function parseISODate(v) {
  if (!v || typeof v !== 'string') return null;
  const d = new Date(`${v.slice(0, 10)}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
}
function toISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function FieldDate({ label, value, onChange }) {
  const { T } = useTheme();
  const { locale } = useCurrency();
  const [open, setOpen] = useState(false);
  const d = parseISODate(value);
  const display = d
    ? d.toLocaleDateString(locale || 'de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '—';
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="wizRow"
        style={{ ...mkFieldBase(T), cursor: 'pointer', textAlign: 'left', width: '100%' }}>
        <div style={mkFieldLabel(T)}>{label}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ ...mkFieldInput(T), flex: 1 }}>{display}</span>
          <svg className="wizDropIco" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <rect x="3" y="4.5" width="18" height="17" rx="3" /><path d="M3 9h18M8 2.5v4M16 2.5v4" />
          </svg>
        </div>
      </button>
      <DatePickerModal open={open} onClose={() => setOpen(false)} value={value} onChange={onChange} label={label} />
    </>
  );
}

// Fixe Zellenhöhe → der Kalender behält IMMER dieselbe Größe, egal ob der Monat
// 4, 5 oder 6 Wochenzeilen hat (immer 6 Reihen gerendert). Body-minHeight hält
// auch beim Umschalten Tage↔Monate die Modal-Größe konstant.
const CAL_CELL_H = 38;
const CAL_BODY_MIN = CAL_CELL_H * 6 + 4 * 5 + 26; // 6 Reihen + Gaps + Wochentags-Header

function DatePickerModal({ open, onClose, value, onChange, label }) {
  const { T } = useTheme();
  const { locale } = useCurrency();
  const { t } = useT();
  const loc = locale || 'de-DE';

  const [view, setView] = useState(() => {
    const s = parseISODate(value) || new Date();
    return new Date(s.getFullYear(), s.getMonth(), 1);
  });
  const [mode, setMode] = useState('days'); // 'days' | 'months'
  // Beim Öffnen auf den Monat des gewählten Datums springen, Tages-Ansicht
  useEffect(() => {
    if (!open) return;
    const s = parseISODate(value) || new Date();
    setView(new Date(s.getFullYear(), s.getMonth(), 1));
    setMode('days');
  }, [open, value]);

  const year = view.getFullYear();
  const month = view.getMonth();
  const todayISO = toISODate(new Date());
  const selDate = parseISODate(value);

  // Wochentags-Header Montag-first, lokalisiert
  const weekdays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => new Date(2023, 0, 2 + i).toLocaleDateString(loc, { weekday: 'short' })),
    [loc]
  );
  const monthNames = useMemo(
    () => Array.from({ length: 12 }, (_, m) => new Date(2023, m, 1).toLocaleDateString(loc, { month: 'short' })),
    [loc]
  );
  const monthLabel = view.toLocaleDateString(loc, { month: 'long', year: 'numeric' });

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7; // Montag = 0
  // IMMER 42 Zellen (6×7) → konstante Höhe unabhängig von der Monatslänge
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length < 42) cells.push(null);

  // Pfeile: Tages-Ansicht = Monat blättern, Monats-Ansicht = Jahr springen (iOS)
  const go = (delta) => setView(mode === 'days'
    ? new Date(year, month + delta, 1)
    : new Date(year + delta, month, 1));
  const pickDay = (d) => { onChange(toISODate(new Date(year, month, d))); onClose(); };
  const pickMonth = (m) => { setView(new Date(year, m, 1)); setMode('days'); };

  // dir: 'left'/'right' = ein Monat, 'left2'/'right2' = ein ganzes JAHR (Doppel-Chevron).
  const NavBtn = ({ dir, onClick }) => {
    const path = {
      left:  'M15 5l-7 7 7 7',
      right: 'M9 5l7 7-7 7',
      left2: 'M18 6l-6 6 6 6 M11 6l-6 6 6 6',
      right2: 'M6 6l6 6-6 6 M13 6l6 6-6 6',
    }[dir];
    const yr = dir.endsWith('2');
    return (
      <button onClick={onClick} aria-label={yr ? (dir[0] === 'l' ? 'Jahr zurück' : 'Jahr vor') : (dir === 'left' ? 'Monat zurück' : 'Monat vor')} style={{
        width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: 'transparent', border: `1px solid ${T.line}`, borderRadius: 999,
        color: T.sub, cursor: 'pointer', WebkitTapHighlightColor: 'transparent', flexShrink: 0,
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d={path} />
        </svg>
      </button>
    );
  };

  const goYear = (delta) => setView(new Date(year + delta, month, 1));

  return (
    <PickerOverlay open={open} onClose={onClose} maxWidth={360}>
      <div style={{ padding: '18px 20px 18px' }}>
        <PickHeader T={T} label={label} onClose={onClose} />

        {/* Titel-Zeile: Titel antippen → zwischen Tages- und Monats-/Jahres-Auswahl wechseln */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '4px 0 14px' }}>
          {/* Tages-Ansicht: Doppel-Chevron springt ein ganzes JAHR, einfach einen Monat. */}
          <div style={{ display: 'flex', gap: 6 }}>
            {mode === 'days' && <NavBtn dir="left2" onClick={() => goYear(-1)} />}
            <NavBtn dir="left" onClick={() => go(-1)} />
          </div>
          <button onClick={() => setMode(mode === 'days' ? 'months' : 'days')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7, background: 'transparent',
              border: 'none', cursor: 'pointer', color: T.text, padding: '4px 8px',
              fontFamily: F.display, fontSize: 18, fontWeight: 700, letterSpacing: '-0.03em',
              textTransform: 'capitalize', WebkitTapHighlightColor: 'transparent',
            }}>
            {mode === 'days' ? monthLabel : year}
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round"
              style={{ transform: mode === 'months' ? 'rotate(180deg)' : 'none', transition: `transform ${DUR.base}ms ease` }}>
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          <div style={{ display: 'flex', gap: 6 }}>
            <NavBtn dir="right" onClick={() => go(1)} />
            {mode === 'days' && <NavBtn dir="right2" onClick={() => goYear(1)} />}
          </div>
        </div>

        <div style={{ minHeight: CAL_BODY_MIN }}>
          {mode === 'days' ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 6 }}>
                {weekdays.map((w, i) => (
                  <div key={i} style={{
                    textAlign: 'center', fontSize: 11, fontWeight: 600, color: T.sub,
                  }}>{w}</div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                {cells.map((d, i) => {
                  if (d === null) return <div key={`b${i}`} style={{ height: CAL_CELL_H }} />;
                  const iso = toISODate(new Date(year, month, d));
                  const isSel = iso === value;
                  const isToday = iso === todayISO;
                  return (
                    <button key={iso} onClick={() => pickDay(d)}
                      onMouseEnter={ev => { if (!isSel) ev.currentTarget.style.background = T.itemHover || 'rgba(255,255,255,0.06)'; }}
                      onMouseLeave={ev => { if (!isSel) ev.currentTarget.style.background = 'transparent'; }}
                      style={{
                        height: CAL_CELL_H, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: 'none', cursor: 'pointer', borderRadius: 999, position: 'relative',
                        background: isSel ? T.red : 'transparent',
                        color: isSel ? '#fff' : T.text,
                        fontFamily: F.family, fontSize: 14, fontWeight: isSel ? 700 : 500,
                        WebkitTapHighlightColor: 'transparent', transition: `background ${DUR.press}ms ease`,
                      }}>
                      {d}
                      {isToday && !isSel && (
                        <span style={{ position: 'absolute', bottom: 5, width: 4, height: 4, borderRadius: 999, background: T.red }} />
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8,
              height: CAL_BODY_MIN, alignContent: 'stretch',
            }}>
              {monthNames.map((mn, m) => {
                const isSel = selDate && selDate.getFullYear() === year && selDate.getMonth() === m;
                const isThisMonth = year === new Date().getFullYear() && m === new Date().getMonth();
                return (
                  <button key={m} onClick={() => pickMonth(m)}
                    onMouseEnter={ev => { if (!isSel) ev.currentTarget.style.background = T.itemHover || 'rgba(255,255,255,0.06)'; }}
                    onMouseLeave={ev => { if (!isSel) ev.currentTarget.style.background = 'transparent'; }}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: 'none', cursor: 'pointer', borderRadius: mr(14),
                      background: isSel ? T.red : 'transparent',
                      color: isSel ? '#fff' : (isThisMonth ? T.red : T.text),
                      fontFamily: F.family, fontSize: 14.5, fontWeight: (isSel || isThisMonth) ? 700 : 500,
                      textTransform: 'capitalize', WebkitTapHighlightColor: 'transparent',
                      transition: `background ${DUR.press}ms ease`,
                    }}>
                    {mn}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <button onClick={() => { onChange(todayISO); onClose(); }}
          style={{
            marginTop: 16, width: '100%', padding: '11px', background: T.glass,
            border: `1px solid ${T.glassBorder}`, borderRadius: mr(12), color: T.text,
            fontFamily: F.family, fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}>{t('tw_date_today')}</button>
      </div>
    </PickerOverlay>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// METHOD STEP — Auswahl Manuell / CSV / API (mit Exchange-Quickrow)
// ═══════════════════════════════════════════════════════════════════════════════
function MethodStep({ onSelect }) {
  const { T } = useTheme();
  const { t, lang } = useT();
  const ICONP = {
    link: 'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07L11.83 5M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71',
    edit: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
    upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12',
    wallet: 'M3 7h15a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7zM16 12h.01M5 7l1.5-3h11L19 7',
  };
  // Methoden in der Pastell-Chip-Sprache (wie die TX-Typen): Börse = Held (Koralle),
  // Manuell/CSV/Wallet kleiner. Weiße Schrift + weißes Icon, helle Prägung (Wasserzeichen).
  const small = [
    { id: 'manual',     chip: '#B49C6E', wm: '#D8C39A', icon: ICONP.edit,   label: t('manual') },
    { id: 'csv',        chip: '#6FA3D4', wm: '#A2C8EC', icon: ICONP.upload, label: 'CSV' },
    { id: 'utxowallet', chip: '#9486CF', wm: '#C2B5ED', icon: ICONP.wallet, label: t('tw_method_utxo_title') },
  ];

  return (
    <div>
      <h2 style={{ margin: '0 0 18px', fontFamily: F.display, fontSize: 24, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.06, color: T.text }}>
        {t('tw_method_title')}
      </h2>

      <div className="wizStagger" style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 22 }}>
        {/* Held — Börse verbinden (empfohlen) */}
        <button onClick={() => onSelect('api')} className="wizPress wizChip"
          style={{
            position: 'relative', overflow: 'hidden', display: 'block', width: '100%', textAlign: 'left',
            cursor: 'pointer', border: 'none', borderRadius: 22, height: 124, padding: 18,
            background: '#DD7D72', color: '#fff', WebkitTapHighlightColor: 'transparent',
            transition: `transform ${DUR.press}ms ${EASE}`,
          }}>
          <svg className="wizChipWm" viewBox="0 0 24 24" fill="none" stroke="#F0AEA4" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"
            style={{ position: 'absolute', right: -20, bottom: -22, width: 124, height: 124, pointerEvents: 'none' }}><path d={ICONP.link} /></svg>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', top: 18, left: 18, zIndex: 2 }}><path d={ICONP.link} /></svg>
          <div style={{ position: 'absolute', left: 18, bottom: 16, zIndex: 2 }}>
            <div style={{ fontFamily: F.brand, fontWeight: 800, fontSize: 22, letterSpacing: '-0.035em', color: '#fff' }}>{lang === 'en' ? 'Connect exchange' : 'Börse verbinden'}</div>
            <div style={{ fontSize: 12, fontWeight: 500, color: '#fff', opacity: 0.82, marginTop: 5 }}>{lang === 'en' ? 'Recommended — syncs automatically' : 'Empfohlen — synchronisiert automatisch'}</div>
          </div>
        </button>

        {/* Manuell · CSV · Wallet */}
        <div style={{ display: 'flex', gap: 11 }}>
          {small.map((m) => (
            <button key={m.id} onClick={() => onSelect(m.id)} className="wizPress wizChip"
              style={{
                position: 'relative', overflow: 'hidden', flex: 1, textAlign: 'left', cursor: 'pointer',
                border: 'none', borderRadius: 18, height: 118, padding: 14,
                background: m.chip, color: '#fff', WebkitTapHighlightColor: 'transparent',
                transition: `transform ${DUR.press}ms ${EASE}`,
              }}>
              <svg className="wizChipWm" viewBox="0 0 24 24" fill="none" stroke={m.wm} strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"
                style={{ position: 'absolute', right: -14, bottom: -16, width: 80, height: 80, pointerEvents: 'none' }}><path d={m.icon} /></svg>
              <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', top: 14, left: 14, zIndex: 2 }}><path d={m.icon} /></svg>
              <span style={{ position: 'absolute', left: 14, bottom: 13, zIndex: 2, fontFamily: F.brand, fontWeight: 800, fontSize: 16, letterSpacing: '-0.03em', color: '#fff' }}>{m.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TYPE STEP — 2×4-Grid mit Icon-Tiles
// ═══════════════════════════════════════════════════════════════════════════════
// EINE gemeinsame Typ-Liste für Erstellen (TypeStep) UND Bearbeiten (Edit-Grid) —
// gleiche Anzahl, gleiche Reihenfolge, gleiche Farben. Logisch gruppiert:
// Handel (Kauf/Verkauf/Tausch) → Transfers (Empfangen/Gesendet) → Einkommen
// (Reward/Airdrop) → Derivate (Futures PnL/Gebühr) → Verlust. Jeder Typ trägt
// seine Farbe (Icon + Label). `EDIT_TYPES` ist nur ein Alias auf diese Liste.
// Pro Typ: color = Original-Akzent (Badges/Border app-weit, unverändert) ·
// chip/wm/ink = Pastell-Chip-Sprache (volle Fläche, helle Prägung, weiße Schrift) ·
// iconPath = neues SVG-Icon (löst die Unicode-Glyphen ab; `icon` bleibt bis zum
// app-weiten Rollout fürs Edit-Grid). Verlust = Minus-Kreis, Futures = Kerzen.
const WIZARD_TYPES = [
  { id: 'kauf',        labelKey: 'buy',             subKey: 'tw_type_buy_sub',      icon: '↓', color: '#2ECC71', chip: '#5FB98A', wm: '#92D6B3', ink: '#1C5436', iconPath: 'M12 3v13M12 16l6-6M12 16l-6-6' },
  { id: 'verkauf',     labelKey: 'sell',            subKey: 'tw_type_sell_sub',     icon: '↑', color: '#E4544A', chip: '#DD7D72', wm: '#F0AEA4', ink: '#7A2A22', iconPath: 'M12 21V8M12 8l6 6M12 8l-6 6' },
  { id: 'swap',        labelKey: 'swap',            subKey: 'tw_type_swap_sub',     icon: '⇄', color: '#888BFF', chip: '#8E90D6', wm: '#B8B9E9', ink: '#2E2F72', iconPath: 'M4 9h13l-4-4M17 9l-4 4M20 15H7l4 4M7 15l4-4' },
  { id: 'einzahlung',  labelKey: 'tw_type_received', subKey: 'tw_type_received_sub', icon: '⇣', color: '#60A5FA', chip: '#74A8DB', wm: '#A6CBEE', ink: '#173E66', iconPath: 'M12 3v11M12 14l4-4M12 14l-4-4M4 19h16' },
  { id: 'auszahlung',  labelKey: 'tw_type_sent',    subKey: 'tw_type_sent_sub',     icon: '⇡', color: '#E67E22', chip: '#E0976A', wm: '#F0BD98', ink: '#6E3D0F', iconPath: 'M22 3L11 14M22 3l-7 19-4-8-8-4z' },
  { id: 'staking',     labelKey: 'tw_type_staking',  subKey: 'tw_type_staking_sub',  icon: '★', color: '#F59E0B', chip: '#D9AE57', wm: '#EDCF8D', ink: '#6E4D09', iconPath: 'M12 3l2.6 6.3 6.8.5-5.2 4.4 1.7 6.6-5.7-3.6-5.7 3.6 1.7-6.6-5.2-4.4 6.8-.5z' },
  { id: 'interest',    labelKey: 'tw_type_interest', subKey: 'tw_type_interest_sub', icon: '%', color: '#FCD34D', chip: '#C9A24E', wm: '#E8D49A', ink: '#5E4A12', iconPath: 'M22 7l-8.5 8.5-5-5L2 17M16 7h6v6' },
  { id: 'airdrop',     labelKey: 'tw_type_airdrop', subKey: 'tw_type_airdrop_sub',  icon: '✦', color: '#A78BFA', chip: '#A78FD9', wm: '#CAB8ED', ink: '#3C2A7E', iconPath: 'M3 12a9 9 0 0 1 18 0M3 12l9 9.5 9-9.5M8.7 12L12 21.5M15.3 12L12 21.5' },
  { id: 'futures_pnl', labelKey: 'tw_type_futures', subKey: 'tw_type_futures_sub',  icon: '⇅', color: '#F472B6', chip: '#DB8DB6', wm: '#EDB8D3', ink: '#7A1F50', iconPath: 'M7 4v3M7 13v3M4.5 7h5v6h-5zM16.5 6v3M16.5 15v3M14 9h5v6h-5z' },
  { id: 'funding_fee', labelKey: 'tw_type_funding', subKey: 'tw_type_funding_sub',  icon: '%', color: '#FB923C', chip: '#DDA15C', wm: '#EFC892', ink: '#724012', iconPath: 'M19 5L5 19M7.6 7.6a1.6 1.6 0 1 1 .01 0M16.4 16.4a1.6 1.6 0 1 1 .01 0' },
  { id: 'loss',        labelKey: 'tw_type_loss',    subKey: 'tw_type_loss_sub',     icon: '⚠', color: '#991B1B', chip: '#C57371', wm: '#DDA2A1', ink: '#5E1414', iconPath: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM8 12h8' },
];

// Fiat-Geld-Buchungen (Bank ↔ Börse) — eigene Erstellen-Chips mit Geldschein-Icon,
// cyan (rein) / rosé (raus). Speichern als bestandsneutrale einzahlung/auszahlung
// mit coin='EUR' (wie buildFiatTx). NUR im Erstellen-TypeStep, NICHT im Edit-Raster
// (dort bliebe die Engine-typ-Kollision mit Empfangen/Gesendet ungelöst).
const FIAT_BANKNOTE = 'M2 7a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2zM12 14.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM5.5 12h.01M18.5 12h.01';
const FIAT_TYPES = [
  { id: 'fiat_in',  labelKey: 'tw_type_fiat_in',  subKey: 'tw_type_fiat_in_sub',  icon: '€', color: '#4FAEC0', chip: '#4FAEC0', wm: '#A9DBE6', ink: '#0E4655', iconPath: FIAT_BANKNOTE },
  { id: 'fiat_out', labelKey: 'tw_type_fiat_out', subKey: 'tw_type_fiat_out_sub', icon: '€', color: '#D488A8', chip: '#D488A8', wm: '#EEC2D9', ink: '#5E2042', iconPath: FIAT_BANKNOTE },
];
// Erstellen-Liste = Standard-Typen + Fiat. Edit nutzt weiter NUR WIZARD_TYPES.
const CREATE_TYPES = [...WIZARD_TYPES, ...FIAT_TYPES];

// Ein Typ-Chip in der finalen Sprache: volle Pastell-Fläche, helles Wasserzeichen
// (eingeprägt), weißes Icon + weißer Name. Auswahl = hochskalieren + Häkchen, der
// Rest dimmt. Mobile = Portrait zum Wischen, Web = kompakt fürs Raster.
function TypeChip({ opt, active, dim, isWide, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="wizChip"
      style={{
        position: 'relative', overflow: 'hidden', cursor: 'pointer', border: 'none',
        background: opt.chip, color: '#fff', padding: isWide ? 14 : 16,
        borderRadius: isWide ? 18 : 22,
        flex: isWide ? undefined : '0 0 128px',
        height: isWide ? 126 : 182,
        scrollSnapAlign: isWide ? undefined : 'center',
        WebkitTapHighlightColor: 'transparent',
        transform: active ? 'scale(1.04)' : 'scale(1)',
        opacity: dim ? 0.5 : 1,
        transition: `transform ${DUR.base}ms ${EASE}, opacity ${DUR.fast}ms ease`,
      }}>
      {/* helles Wasserzeichen, beschnitten von der Chip-Kante */}
      <svg className="wizChipWm" viewBox="0 0 24 24" fill="none" stroke={opt.wm} strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"
        style={{ position: 'absolute', right: isWide ? -18 : -24, bottom: isWide ? -18 : -24, width: isWide ? 96 : 140, height: isWide ? 96 : 140, pointerEvents: 'none' }}>
        <path d={opt.iconPath} />
      </svg>
      {/* Häkchen bei Auswahl */}
      {active && (
        <span style={{ position: 'absolute', top: 11, right: 11, zIndex: 3, width: 22, height: 22, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={opt.ink} strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12l5 5L20 6" /></svg>
        </span>
      )}
      {/* Icon oben + Label mittig darunter (zentriert, langes Label bricht um) —
          so passen auch „Einzahlung/Auszahlung/Empfangen/Gesendet" ins schmale Web-Raster. */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: isWide ? 9 : 11, padding: isWide ? '6px 5px' : '10px 8px', textAlign: 'center' }}>
        <svg width={isWide ? 23 : 28} height={isWide ? 23 : 28} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
          <path d={opt.iconPath} />
        </svg>
        <span style={{ fontFamily: F.brand, fontWeight: 800, fontSize: isWide ? 12.5 : 17, letterSpacing: '-0.02em', color: '#fff', lineHeight: 1.12, overflowWrap: 'break-word', maxWidth: '100%' }}>
          {label}
        </span>
      </div>
    </button>
  );
}

// Detail-/Bearbeiten-Kopf in der Typ-Pastell-Sprache (Bild 2): flache, breite Bahn
// in der Typ-Farbe, weiße Schrift, Coin-Logo links, großes helles Wasserzeichen
// mittig im weißen Bereich. Ersetzt den alten „SYM · Typ"-StepHero.
function TxTypeBanner({ typInfo, label, selectedCoin, marketData, priceText }) {
  const chip = typInfo?.chip || '#8E90D6';
  const wm   = typInfo?.wm   || '#B8B9E9';
  const path = typInfo?.iconPath;
  return (
    <div style={{
      position: 'relative', overflow: 'hidden', borderRadius: 20,
      background: chip, color: '#fff', padding: '15px 18px', marginBottom: 22,
      minHeight: 84, display: 'flex', alignItems: 'center', gap: 13,
    }}>
      {path && (
        <svg viewBox="0 0 24 24" fill="none" stroke={wm} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"
          style={{ position: 'absolute', right: 22, top: '50%', transform: 'translateY(-50%)', width: 100, height: 100, pointerEvents: 'none' }}>
          <path d={path} />
        </svg>
      )}
      <span style={{ position: 'relative', zIndex: 2, width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <CoinImage sym={selectedCoin?.sym} marketData={marketData} size={30} />
      </span>
      <div style={{ position: 'relative', zIndex: 2, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {path && (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d={path} /></svg>
          )}
          <span style={{ fontFamily: F.brand, fontWeight: 800, fontSize: 20, letterSpacing: '-0.035em', color: '#fff', lineHeight: 1 }}>
            {selectedCoin?.sym ? `${selectedCoin.sym} · ` : ''}{label}
          </span>
        </div>
        {priceText && (
          <div style={{ fontSize: 12, fontWeight: 500, color: '#fff', opacity: 0.82, marginTop: 6 }}>{priceText}</div>
        )}
      </div>
    </div>
  );
}

function TypeStep({ typ, setTyp, onNext }) {
  const { T } = useTheme();
  const { t } = useT();
  const { isWide } = useBreakpoint();
  return (
    <div>
      <StepHero
        title={t('tw_type_title')}
        sub={t('tw_type_sub')}
      />

      {/* Mobile: horizontale Chip-Reihe (wischbar). Web: 5×2-Raster (kein Scroll). */}
      <div
        className="wizStagger"
        style={isWide
          ? { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 22 }
          : { display: 'flex', gap: 12, overflowX: 'auto', WebkitOverflowScrolling: 'touch', margin: '0 -16px 22px', padding: '4px 16px 8px', scrollSnapType: 'x proximity', scrollbarWidth: 'none' }
        }
      >
        {CREATE_TYPES.map(opt => (
          <TypeChip
            key={opt.id}
            opt={opt}
            label={t(opt.labelKey)}
            active={typ === opt.id}
            dim={!!typ && typ !== opt.id}
            isWide={isWide}
            onClick={() => setTyp(opt.id)}
          />
        ))}
      </div>

      <button onClick={onNext} disabled={!typ} style={typ ? mkBtnPrimary(T) : mkBtnDisabled(T)}>{t('next')} →</button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COIN STEP — Suchfeld + Liste mit großen Rows
// ═══════════════════════════════════════════════════════════════════════════════
function CoinStep({ selectedCoin, setSelectedCoin, marketData, onNext }) {
  const { T } = useTheme();
  const { t } = useT();
  const { convert, symbol, locale } = useCurrency();
  const [search, setSearch] = useState('');
  const [hoverSym, setHoverSym] = useState(null);
  const filtered = COIN_LIST.filter(c =>
    c.sym.toLowerCase().includes(search.toLowerCase()) ||
    c.name.toLowerCase().includes(search.toLowerCase())
  ).slice(0, 10);

  return (
    <div>
      <StepHero
        title={selectedCoin ? selectedCoin.name : t('tw_coin_title')}
        sub={selectedCoin ? t('tw_coin_sub_selected') : t('tw_coin_sub')}
        icon={selectedCoin ? <CoinImage sym={selectedCoin.sym} marketData={marketData} size={26} /> : undefined}
      />

      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
        background: T.surfaceHi || T.surface,
        border: `1px solid ${T.line || T.border}`,
        borderRadius: R.input, marginBottom: 6,
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke={T.sub} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('tw_coin_search_ph')}
          style={{
            background: 'none', border: 'none', outline: 'none',
            color: T.text, fontFamily: F.family, fontSize: 14, width: '100%',
          }}
        />
      </div>

      {/* Flache Liste: Haarlinien-Zeilen, kein umrandeter Glass-Container. */}
      <div style={{ marginBottom: 22, maxHeight: 360, overflowY: 'auto' }}>
        {filtered.map((c, i) => {
          const isSelected = selectedCoin?.sym === c.sym;
          const mData = marketData?.[c.id];
          return (
            <div key={c.sym}
              onClick={() => { setSelectedCoin(c); setSearch(''); }}
              className="wizRow"
              onMouseEnter={() => setHoverSym(c.sym)}
              onMouseLeave={() => setHoverSym(h => h === c.sym ? null : h)}
              style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '13px 2px',
              cursor: 'pointer', minHeight: 56,
              background: (!isSelected && hoverSym === c.sym) ? (T.itemHover || 'transparent') : 'transparent',
              borderTop: `1px solid ${T.line || T.border}`,
              borderBottom: i === filtered.length - 1 ? `1px solid ${T.line || T.border}` : 'none',
              transition: `background ${DUR.press}ms ${EASE}`,
            }}>
              <span className="wizLeadIco" style={{ display: 'inline-flex', flexShrink: 0 }}><CoinImage sym={c.sym} marketData={marketData} size={36} /></span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: F.family }}>{c.sym}</div>
                <div style={{ fontSize: 11.5, color: T.sub, fontFamily: F.family }}>{c.name}</div>
              </div>
              {mData?.price && (
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: F.family }}>
                  {fmt(convert(mData.price), locale)} {symbol}
                </div>
              )}
              {isSelected && <Icon d={Icons.check} size={18} stroke={T.red} sw={2.6} style={{ marginLeft: 4, flexShrink: 0 }} />}
            </div>
          );
        })}
      </div>

      <button onClick={onNext} disabled={!selectedCoin} style={selectedCoin ? mkBtnPrimary(T) : mkBtnDisabled(T)}>
        {t('next')} →
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SOURCE STEP — NEU: Eigene Wallet / Freund / Geschenk
// ═══════════════════════════════════════════════════════════════════════════════
// Pastell-Auswahl-Card (Quelle/„Wo gekauft?"): volle Typ-Farbe, weißes Icon im
// hellen Ring, weißer Titel + Beschreibung, helles Wasserzeichen unten rechts.
// `glyph` = SVG-Inhalt (Pfade/Kreise), wird weiß (Ring) UND hell (Wasserzeichen)
// gerendert. Keine Icon-Kachel, kein Chevron, keine Caps — die Typ-Chip-Sprache.
function PickCard({ chip, wm, glyph, title, sub, onClick }) {
  return (
    <button onClick={onClick} className="wizPress wizChip" style={{
      position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', gap: 14,
      width: '100%', textAlign: 'left', cursor: 'pointer', border: 'none', borderRadius: 22,
      padding: '17px 18px', marginBottom: 12, minHeight: 96, background: chip, color: '#fff',
      WebkitTapHighlightColor: 'transparent', transition: `transform ${DUR.press}ms ${EASE}`,
    }}>
      <svg className="wizChipWm" viewBox="0 0 24 24" fill="none" stroke={wm} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"
        style={{ position: 'absolute', right: -16, bottom: -20, width: 104, height: 104, pointerEvents: 'none' }}>{glyph}</svg>
      <span style={{ position: 'relative', zIndex: 2, width: 46, height: 46, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">{glyph}</svg>
      </span>
      <span style={{ position: 'relative', zIndex: 2, minWidth: 0 }}>
        <span style={{ display: 'block', fontFamily: F.brand, fontWeight: 800, fontSize: 17, letterSpacing: '-0.03em', lineHeight: 1.14, color: '#fff' }}>{title}</span>
        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 500, color: '#fff', opacity: 0.82, marginTop: 5, lineHeight: 1.38 }}>{sub}</span>
      </span>
    </button>
  );
}

// Glyphen für die Quelle-/Kauf-Cards (Linien-SVG, weiß bzw. hell gerendert).
const PICK_GLYPHS = {
  wallet: (<><path d="M3 7h15a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" /><path d="M16 12h.01" /><path d="M5 7l1.5-3h11L19 7" /></>),
  person: (<><circle cx="12" cy="8" r="3.6" /><path d="M5 20.5c.8-3.8 3.8-6 7-6s6.2 2.2 7 6" /></>),
  gift:   (<><rect x="3.5" y="10" width="17" height="10.5" rx="1.6" /><path d="M3 10h18M12 10v10.5" /><path d="M12 10s-3-1.5-3-3.8A2.2 2.2 0 0 1 12 4.4 2.2 2.2 0 0 1 15 6.2c0 2.3-3 3.8-3 3.8z" /></>),
  bank:   (<path d="M3 21h18M5 21V9l7-5 7 5v12M9 21v-6h6v6" />),
  card:   (<><rect x="2.5" y="6" width="19" height="12" rx="2" /><path d="M2.5 10h19" /></>),
};

function SourceStep({ typ, selectedCoin, marketData, onSelect }) {
  const { t } = useT();
  const isIn = typ === 'einzahlung';

  const sources = [
    { id: 'own',      chip: '#74A8DB', wm: '#A6CBEE', glyph: PICK_GLYPHS.wallet, title: isIn ? t('tw_src_own_in') : t('tw_src_own_out'), sub: t('tw_src_own_sub') },
    { id: 'external', chip: '#E0976A', wm: '#F0BD98', glyph: PICK_GLYPHS.person, title: isIn ? t('tw_src_ext_in') : t('tw_src_ext_out'), sub: t('tw_src_ext_sub') },
    { id: 'gift',     chip: '#A78FD9', wm: '#CAB8ED', glyph: PICK_GLYPHS.gift,   title: isIn ? t('tw_src_gift_in') : t('tw_src_gift_out'), sub: isIn ? t('tw_src_gift_sub_in') : t('tw_src_gift_sub_out') },
  ];

  return (
    <div>
      <StepHero
        title={t('tw_src_title')}
        sub={`${selectedCoin?.name || t('coin')} · ${t('tw_src_sub')}`}
        icon={<CoinImage sym={selectedCoin?.sym} marketData={marketData} size={26} />}
      />
      <div style={{ marginBottom: 22 }}>
        {sources.map(s => (
          <PickCard key={s.id} chip={s.chip} wm={s.wm} glyph={s.glyph} title={s.title} sub={s.sub} onClick={() => onSelect(s.id)} />
        ))}
      </div>
    </div>
  );
}

// „Wo gekauft?" — eigener Kauf-Schritt nach dem Coin (Bild 2). Direkt auf der
// Börse → Börsen-Picker; Über Zahlungsdienstleister → Dienstleister + Ziel
// (MoonPay → Ledger). Setzt exchange (+ wallet) und geht dann ins Formular.
function BuySourceStep({ selectedCoin, marketData, setExchange, setWallet, onNext }) {
  const { t } = useT();
  const [phase, setPhase] = useState('cards'); // cards|direct|providers|dest
  const [chosenProvider, setChosenProvider] = useState(null);
  const close = () => { setPhase('cards'); setChosenProvider(null); };

  return (
    <div>
      <StepHero
        title={t('tw_src_root_title')}
        icon={<CoinImage sym={selectedCoin?.sym} marketData={marketData} size={26} />}
      />
      <div style={{ marginBottom: 22 }}>
        <PickCard chip="#5FB98A" wm="#92D6B3" glyph={PICK_GLYPHS.bank}
          title={t('tw_src_direct')} sub={t('tw_src_direct_sub')} onClick={() => setPhase('direct')} />
        <PickCard chip="#6FA3D4" wm="#A2C8EC" glyph={PICK_GLYPHS.card}
          title={t('tw_src_provider')} sub={t('tw_src_provider_sub')} onClick={() => setPhase('providers')} />
      </div>

      {/* Direkt: Börse wählen → Formular */}
      <ExchangePickerModal
        open={phase === 'direct'} onClose={close} value={null}
        onChange={(lbl) => { setExchange(lbl); setWallet(''); close(); onNext(); }}
        label={t('tw_src_root_title')} onBack={() => setPhase('cards')}
      />
      {/* Dienstleister wählen → Ziel-Schritt */}
      <ExchangePickerModal
        open={phase === 'providers'} onClose={close} value={null}
        onChange={(lbl) => { setChosenProvider(lbl); setPhase('dest'); }}
        label={t('tw_src_root_title')} customOptions={PROVIDER_LABELS}
        titleKey="tw_pick_provider_title" onBack={() => setPhase('cards')} closeOnPick={false}
      />
      {/* Ziel-Börse/Wallet wählen → Formular */}
      <ExchangePickerModal
        open={phase === 'dest'} onClose={close} value={null}
        onChange={(lbl) => { setExchange(chosenProvider); setWallet(lbl); close(); onNext(); }}
        label={t('tw_src_root_title')} titleKey="tw_pick_dest_title" onBack={() => setPhase('providers')}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DETAILS STEP — Trade-Formular (Kauf/Verkauf/Income/Loss)
// ═══════════════════════════════════════════════════════════════════════════════
function DetailsStep({
  typ, selectedCoin, marketData,
  datum, setDatum, menge, setMenge, preis, setPreis,
  gebuehr, setGebuehr, hash, setHash, exchange, setExchange,
  wallet, setWallet,
  notiz, setNotiz, onNext,
}) {
  const { T } = useTheme();
  const { t } = useT();
  const { convert, symbol, locale } = useCurrency();
  const typInfo  = WIZARD_TYPES.find(x => x.id === typ);
  const isIncome = typ === 'staking' || typ === 'interest' || typ === 'airdrop';
  const isLoss   = typ === 'loss';
  const isDeriv  = typ === 'futures_pnl' || typ === 'funding_fee';
  const needsPriceLocal = typ === 'kauf' || typ === 'verkauf';
  const stueck = needsPriceLocal && menge && preis ? (parseFloat(preis) / parseFloat(menge)).toFixed(4) : null;
  const mData = marketData?.[selectedCoin?.id];
  // Derivat (Futures PnL / Funding-Fee): nur ein SIGNIERTER €-Betrag, keine Menge.
  const canNext = isDeriv
    ? (!!datum && preis !== '' && Number.isFinite(parseFloat(preis)))
    : (menge && parseFloat(menge) > 0 && datum && (isIncome || isLoss || preis));

  const [fetchingPrice, setFetchingPrice] = useState(false);
  const fetchPrice = async () => {
    if (!selectedCoin?.id || !datum || !menge || parseFloat(menge) <= 0) return;
    setFetchingPrice(true);
    try {
      // Geht zuerst über /api/coin-price-history (CoinGecko Demo-Key), Fallback auf
      // öffentlichen market_chart-Endpoint (≤365d). Direkter /history-Call würde
      // 401 zurückgeben — der frühere Code dieser Stelle war stumm broken.
      const unitPrice = await getHistoricalPriceAt(selectedCoin.id, datum, 'eur');
      if (typeof unitPrice === 'number' && unitPrice > 0) {
        setPreis(String(Math.round(parseFloat(menge) * unitPrice * 100) / 100));
      }
    } finally { setFetchingPrice(false); }
  };

  return (
    <div>
      <TxTypeBanner
        typInfo={typInfo}
        label={typInfo ? t(typInfo.labelKey) : t('tw_details_eyebrow')}
        selectedCoin={selectedCoin}
        marketData={marketData}
        priceText={mData?.price ? `${t('current_price')}: ${fmt(convert(mData.price), locale)} ${symbol}` : null}
      />

      <div style={fieldGroupStyle(T)}>
        <FieldRow label={t('date')} type="date" value={datum} onChange={setDatum} />
        {isDeriv && (
          <div style={mkFieldBase(T)}>
            <div style={mkFieldLabel(T)}>{t('tw_pnl_label')} ({symbol})</div>
            <input
              type="text" inputMode="decimal"
              value={preis} onChange={e => setPreis(e.target.value)}
              placeholder={t('tw_pnl_ph')}
              style={{ ...mkFieldInput(T), width: '100%' }}
            />
            <div style={{ fontSize: 11, color: T.sub, marginTop: 6, fontFamily: F.family }}>
              {t('tw_pnl_hint')}
            </div>
          </div>
        )}
        {!isDeriv && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0 }}>
          <FieldNum label={`${t('amount')} (${selectedCoin?.sym || t('coin')})`} placeholder="0.00" value={menge} onChange={setMenge} />
          {needsPriceLocal && (
            <div style={mkFieldBase(T)}>
              {/* Label links, berechneter Ø-Stückpreis rechts daneben — spart die
                  eigene Stückpreis-Box im Formular. */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
                <span style={{ ...mkFieldLabel(T), marginBottom: 0 }}>{t('tw_total')} ({symbol})</span>
                {stueck && (
                  <span style={{ fontSize: 11, color: T.sub, fontWeight: 600, fontFamily: F.family, whiteSpace: 'nowrap' }}>
                    ≈ {fmt(convert(parseFloat(stueck)), locale)} {symbol} / {selectedCoin?.sym}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="text" inputMode="decimal"
                  value={preis} onChange={e => setPreis(e.target.value)}
                  placeholder="0.00"
                  style={{ ...mkFieldInput(T), flex: 1, minWidth: 0 }}
                />
                <RateButton onClick={fetchPrice} disabled={!menge || !datum || fetchingPrice} fetching={fetchingPrice} />
              </div>
            </div>
          )}
        </div>
        )}
        {isIncome && (
          <FieldNum label={t('tw_value_at_time')} placeholder="0.00" value={preis} onChange={setPreis} />
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0 }}>
          <FieldNum label={`${t('tw_fee')} (${symbol})`} placeholder="0.00" value={gebuehr} onChange={setGebuehr} />
          {/* Nur Kauf: Schritt-0 (direkt vs. Dienstleister) → „MoonPay → Ledger". */}
          <FieldSelect
            label={t('tw_exchange')}
            value={exchange}
            onChange={(ex, wal) => { setExchange(ex); if (typ === 'kauf' && setWallet) setWallet(wal || ''); }}
            options={EXCHANGES}
            withExchangeLogo
            allowProvider={typ === 'kauf'}
            wallet={wallet}
          />
        </div>
        <FieldRow label={t('tw_txhash_opt')} placeholder="0x..." value={hash} onChange={setHash} />
        <FieldRow label={t('tw_note_opt')} placeholder={t('tw_note_ph_dca')} value={notiz} onChange={setNotiz} />
      </div>

      <button onClick={onNext} disabled={!canNext} style={canNext ? mkBtnPrimary(T) : mkBtnDisabled(T)}>
        {t('next')} →
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// INLINE COIN PICKER (für Swap)
// ═══════════════════════════════════════════════════════════════════════════════
function InlineCoinPicker({ label, selected, onSelect, marketData }) {
  const { T } = useTheme();
  const { t } = useT();
  const { convert, symbol, locale } = useCurrency();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const filtered = COIN_LIST.filter(c =>
    c.sym.toLowerCase().includes(search.toLowerCase()) ||
    c.name.toLowerCase().includes(search.toLowerCase())
  ).slice(0, 6);

  return (
    <div>
      <div style={mkFieldLabel(T)}>{label}</div>
      {selected && !open ? (
        <div
          onClick={() => setOpen(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
            background: T.surfaceHi || T.surface,
            border: `1px solid ${T.line || T.border}`, borderRadius: R.input, cursor: 'pointer',
          }}
        >
          <CoinImage sym={selected.sym} marketData={marketData} size={28} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: F.family }}>{selected.sym}</div>
            <div style={{ fontSize: 11, color: T.sub, fontFamily: F.family }}>{selected.name}</div>
          </div>
          <span style={{ color: T.sub, fontSize: 12 }}>↻ {t('tw_change')}</span>
        </div>
      ) : (
        <div style={{ background: T.surfaceHi || T.surface, border: `1px solid ${T.line || T.border}`, borderRadius: R.input, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: `1px solid ${T.line || T.border}` }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" /></svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('tw_coin_search_inline')}
              style={{ background: 'none', border: 'none', outline: 'none', color: T.text, fontFamily: F.family, fontSize: 14, width: '100%' }}
            />
            {selected && <span onClick={() => setOpen(false)} style={{ color: T.sub, cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</span>}
          </div>
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {filtered.map((c, i) => (
              <div key={c.sym} onClick={() => { onSelect(c); setSearch(''); setOpen(false); }} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer',
                background: selected?.sym === c.sym ? T.surfaceHigh : 'transparent',
                borderBottom: i < filtered.length - 1 ? `1px solid ${T.glassBorder}` : 'none',
              }}>
                <CoinImage sym={c.sym} marketData={marketData} size={28} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: F.family }}>{c.sym}</div>
                  <div style={{ fontSize: 11, color: T.sub, fontFamily: F.family }}>{c.name}</div>
                </div>
                {marketData?.[c.id]?.price && (
                  <div style={{ fontSize: 12, color: T.sub, fontFamily: F.family }}>{fmt(convert(marketData[c.id].price), locale)} {symbol}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SWAP DETAILS STEP
// ═══════════════════════════════════════════════════════════════════════════════
function SwapDetailsStep({
  marketData,
  datum, setDatum,
  coinOut, setCoinOut, mengeOut, setMengeOut,
  coinIn, setCoinIn, mengeIn, setMengeIn,
  preis, setPreis,
  gebuehr, setGebuehr,
  exchange, setExchange,
  hash, setHash,
  notiz, setNotiz,
  onNext,
}) {
  const { T } = useTheme();
  const { t } = useT();
  const { symbol } = useCurrency();
  const [fetchingPrice, setFetchingPrice] = useState(false);

  const fetchPrice = async () => {
    if (!coinOut || !datum || !mengeOut) return;
    setFetchingPrice(true);
    try {
      // Proxy-Pfad mit Demo-Key + market_chart-Fallback. Direkter /history-Call
      // (vorher hier) wäre 401.
      const unitPrice = await getHistoricalPriceAt(coinOut.id, datum, 'eur');
      if (typeof unitPrice === 'number' && unitPrice > 0) {
        setPreis(String(Math.round(parseFloat(mengeOut) * unitPrice * 100) / 100));
      }
    } finally { setFetchingPrice(false); }
  };

  const canNext = coinOut && coinIn && mengeOut && mengeIn && preis && datum;

  return (
    <div>
      <StepHero
        accent="#888BFF"
        eyebrow={t('swap')}
        title={t('tw_swap_title')}
        sub={t('tw_swap_sub')}
        icon="⇄"
      />

      <div style={fieldGroupStyle(T)}>
        <InlineCoinPicker label={t('tw_swap_coin_out')} selected={coinOut} onSelect={setCoinOut} marketData={marketData} />
        <FieldNum label={`${t('tw_swap_amount_out')}${coinOut ? ` (${coinOut.sym})` : ''}`} placeholder="0.00" value={mengeOut} onChange={setMengeOut} />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.sub, fontSize: 20, margin: '2px 0' }}>↓</div>

        <InlineCoinPicker label={t('tw_swap_coin_in')} selected={coinIn} onSelect={setCoinIn} marketData={marketData} />
        <FieldNum label={`${t('tw_swap_amount_in')}${coinIn ? ` (${coinIn.sym})` : ''}`} placeholder="0.00" value={mengeIn} onChange={setMengeIn} />

        <div style={mkFieldBase(T)}>
          <div style={mkFieldLabel(T)}>{t('tw_swap_value')}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="text" inputMode="decimal"
              value={preis} onChange={e => setPreis(e.target.value)}
              placeholder="0.00"
              style={{ ...mkFieldInput(T), flex: 1, minWidth: 0 }}
            />
            <RateButton onClick={fetchPrice} disabled={!coinOut || !datum || !mengeOut || fetchingPrice} fetching={fetchingPrice} />
          </div>
        </div>

        <FieldRow label={t('date')} type="date" value={datum} onChange={setDatum} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0 }}>
          <FieldNum label={`${t('tw_fee')} (${symbol})`} placeholder="0.00" value={gebuehr} onChange={setGebuehr} />
          <FieldSelect label={t('tw_exchange')} value={exchange} onChange={setExchange} options={EXCHANGES} withExchangeLogo />
        </div>
        <FieldRow label={t('tw_txhash_opt')} placeholder="0x..." value={hash} onChange={setHash} />
        <FieldRow label={t('tw_note_opt')} placeholder={t('tw_note_ph_uniswap')} value={notiz} onChange={setNotiz} />
      </div>

      <button onClick={onNext} disabled={!canNext} style={canNext ? mkBtnPrimary(T) : mkBtnDisabled(T)}>
        {t('next')} →
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TRANSFER DETAILS STEP (für eigene Wallet / extern)
// ═══════════════════════════════════════════════════════════════════════════════
function TransferDetailsStep({
  typ, selectedCoin, marketData, sourceMode,
  datum, setDatum, menge, setMenge, gebuehr, setGebuehr,
  hash, setHash, exchange, setExchange, notiz, setNotiz,
  onNext,
}) {
  const { T } = useTheme();
  const { t } = useT();
  const isIn = typ === 'einzahlung';
  const accent = sourceMode === 'external' ? '#F59E0B' : (isIn ? '#60A5FA' : '#E67E22');
  const canNext = menge && parseFloat(menge) > 0 && datum;

  return (
    <div>
      <StepHero
        accent={accent}
        eyebrow={sourceMode === 'external' ? t('tw_transfer_external') : (isIn ? t('tw_type_received') : t('tw_type_sent'))}
        title={isIn ? t('tw_transfer_in_title') : t('tw_transfer_out_title')}
        sub={`${selectedCoin?.name || t('coin')} · ${t('tw_transfer_sub')}`}
        icon={<CoinImage sym={selectedCoin?.sym} marketData={marketData} size={28} />}
      />

      <div style={fieldGroupStyle(T)}>
        <FieldRow label={t('date')} type="date" value={datum} onChange={setDatum} />
        <FieldNum label={`${t('amount')} (${selectedCoin?.sym || t('coin')})`} placeholder="0.00" value={menge} onChange={setMenge} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0 }}>
          <FieldNum label={t('tw_network_fee')} placeholder="0.00" value={gebuehr} onChange={setGebuehr} />
          <FieldSelect label={isIn ? t('tw_to_exchange') : t('tw_from_exchange')} value={exchange} onChange={setExchange} options={EXCHANGES} withExchangeLogo />
        </div>
        <FieldRow label={t('tw_txhash_opt')} placeholder="0x..." value={hash} onChange={setHash} />
        <FieldRow label={t('tw_note_opt')} placeholder={t('tw_note_ph_from')} value={notiz} onChange={setNotiz} />
      </div>

      {sourceMode === 'external' && (
        <div style={{ marginBottom: 14 }}>
          <InfoBanner tone="warning" title={t('tw_transfer_external')}>
            {t('tw_external_banner')}
          </InfoBanner>
        </div>
      )}

      <button onClick={onNext} disabled={!canNext} style={canNext ? mkBtnPrimary(T) : mkBtnDisabled(T)}>
        {t('next')} →
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TRANSFER CONFIRM STEP — Gegenstück-Auswahl (nur sourceMode='own')
// ═══════════════════════════════════════════════════════════════════════════════
function TransferConfirmStep({
  typ, selectedCoin, marketData, datum, menge, gebuehr, exchange, hash, notiz,
  existingTxs, onSave,
}) {
  const { T } = useTheme();
  const { t } = useT();
  const isIn = typ === 'einzahlung';
  const oppositeTyp = isIn ? 'auszahlung' : 'einzahlung';
  const accent = isIn ? '#60A5FA' : '#E67E22';

  const [selectedPartner, setSelectedPartner] = useState(null);

  const candidates = useMemo(() => {
    if (!existingTxs?.length || !selectedCoin) return [];
    const base   = new Date(datum).getTime();
    const window = 30 * 24 * 3600 * 1000;
    return existingTxs
      .filter(t =>
        t.typ === oppositeTyp &&
        t.coin === selectedCoin.sym &&
        !t.linkedTxId && !t.externalTransfer &&
        Math.abs(new Date(t.datum).getTime() - base) <= window
      )
      .sort((a, b) =>
        Math.abs(new Date(a.datum).getTime() - base) -
        Math.abs(new Date(b.datum).getTime() - base)
      )
      .slice(0, 5);
  }, [existingTxs, oppositeTyp, selectedCoin, datum]);

  const handleSave = () => {
    const linkInfo = selectedPartner
      ? { docId: selectedPartner._docId || String(selectedPartner.id) }
      : null;
    onSave(linkInfo, false);
  };

  return (
    <div>
      <StepHero
        accent={accent}
        eyebrow={t('confirm')}
        title={t('tw_confirm_title')}
        sub={t('tw_confirm_link_sub')}
        icon={<CoinImage sym={selectedCoin?.sym} marketData={marketData} size={28} />}
      />

      <div style={{
        background: T.surface, border: `1px solid ${T.border}`,
        borderRadius: mr(16), overflow: 'hidden', marginBottom: 16,
        boxShadow: T.cardShadow,
      }}>
        <div style={{
          padding: '16px', background: T.surfaceHi,
          borderBottom: `1px solid ${T.line}`,
          display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <CoinImage sym={selectedCoin?.sym} marketData={marketData} size={44} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: F.family }}>
              {menge} {selectedCoin?.sym} {isIn ? t('tw_received_verb') : t('tw_sent_verb')}
            </div>
            <div style={{ fontSize: 12.5, color: T.sub, fontFamily: F.family, marginTop: 2 }}>
              {selectedCoin?.name} · {datum}
            </div>
          </div>
        </div>
        {[
          [isIn ? t('tw_to_exchange') : t('tw_from_exchange'), exchange],
          gebuehr && parseFloat(gebuehr) > 0 ? [t('tw_network_fee'), `${gebuehr} ${selectedCoin?.sym}`] : null,
          hash ? [t('tw_txhash'), hash.length > 20 ? hash.slice(0, 20) + '…' : hash] : null,
          notiz ? [t('tw_note'), notiz] : null,
        ].filter(Boolean).map(([k, v], i, arr) => (
          <div key={k} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 16px', borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : 'none',
          }}>
            <span style={{ fontSize: 12, color: T.sub, fontFamily: F.family }}>{k}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: F.family }}>{v}</span>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{
          fontSize: 11.5, fontWeight: 600, color: T.sub, fontFamily: F.family,
          marginBottom: 8, letterSpacing: '-0.005em',
        }}>
          {t('tw_link_counterpart')}
        </div>
        {candidates.length === 0 ? (
          <InfoBanner tone="info" title={t('tw_no_counterpart_title')}>
            {t('tw_no_counterpart_sub')}
          </InfoBanner>
        ) : (
          <div style={{ border: `1px solid ${T.border}`, borderRadius: mr(14), overflow: 'hidden' }}>
            {candidates.map((c, i) => {
              const cId = c._docId || String(c.id);
              const isSel = (selectedPartner?._docId || String(selectedPartner?.id)) === cId;
              return (
                <div
                  key={cId}
                  onClick={() => setSelectedPartner(isSel ? null : c)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 14px', cursor: 'pointer',
                    background: isSel ? withAlpha(accent, 0.12) : T.surface,
                    borderBottom: i < candidates.length - 1 ? `1px solid ${T.border}` : 'none',
                    transition: `background ${DUR.press}ms ${EASE}`,
                  }}
                >
                  <div style={{
                    width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                    background: isSel ? withAlpha(accent, 0.22) : T.glass,
                    border: `1.5px solid ${isSel ? accent : T.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, color: accent, fontWeight: 700,
                  }}>{isSel ? '✓' : (c.typ === 'einzahlung' ? '↓' : '↑')}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: F.family }}>
                      {c.menge} {c.coin} {c.typ === 'einzahlung' ? t('tw_received_verb') : t('tw_sent_verb')}
                    </div>
                    <div style={{ fontSize: 11, color: T.sub, fontFamily: F.family }}>
                      {c.datum} · {c.exchange}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <button onClick={handleSave} style={mkBtnPrimary(T)}>
        {selectedPartner ? `⛓ ${t('tw_save_and_link')}` : `✓ ${t('tw_save_transfer')}`}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONFIRM STEP — Review (Trade/Swap/Income/Loss/External)
// ═══════════════════════════════════════════════════════════════════════════════
function ConfirmStep({
  typ, selectedCoin, marketData, datum, menge, preis, gebuehr,
  hash, exchange, notiz, swapCoinOut, swapMengeOut, swapCoinIn, swapMengeIn,
  isSwap, sourceMode, onSave,
}) {
  const { T } = useTheme();
  const { t } = useT();
  const { convert, symbol, locale } = useCurrency();
  const typInfo = WIZARD_TYPES.find(x => x.id === typ);
  const isDeriv = typ === 'futures_pnl' || typ === 'funding_fee';
  // Derivat: kein Stückpreis (menge=0), preis ist der signierte €-PnL.
  const stueck = !isSwap && !isDeriv && menge && preis ? (parseFloat(preis) / parseFloat(menge)).toFixed(4) : null;
  const typVerb = {
    kauf: t('tw_verb_kauf'), verkauf: t('tw_verb_verkauf'),
    staking: t('tw_verb_staking'), airdrop: t('tw_verb_airdrop'),
    loss: t('tw_verb_loss'), einzahlung: t('tw_verb_einzahlung'), auszahlung: t('tw_verb_auszahlung'),
  }[typ] || typ;

  return (
    <div>
      <StepHero
        title={t('tw_confirm_title')}
        sub={t('tw_confirm_sub')}
      />

      {/* Flach: Coin + Klartext-Kopf, dann Haarlinien-Zeilen (keine Karten-Box). */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '4px 0 16px' }}>
          {isSwap ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <CoinImage sym={swapCoinOut?.sym} marketData={marketData} size={36} />
              <span style={{ color: T.sub, fontSize: 18, fontWeight: 700 }}>→</span>
              <CoinImage sym={swapCoinIn?.sym} marketData={marketData} size={36} />
            </div>
          ) : (
            <CoinImage sym={selectedCoin?.sym} marketData={marketData} size={44} />
          )}
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: F.family }}>
              {isSwap
                ? `${swapMengeOut} ${swapCoinOut?.sym} → ${swapMengeIn} ${swapCoinIn?.sym}`
                : isDeriv
                  ? `${fmt(convert(parseFloat(preis) || 0), locale)} ${symbol} · ${selectedCoin?.sym}`
                  : `${menge} ${selectedCoin?.sym} ${typVerb}`}
            </div>
            <div style={{ fontSize: 12.5, color: T.sub, fontFamily: F.family, marginTop: 2 }}>
              {isSwap ? datum : `${selectedCoin?.name} · ${datum}`}
              {sourceMode === 'gift' && ` · ${t('tw_gift')}`}
            </div>
          </div>
        </div>
        {[
          preis ? [isDeriv ? t('tw_pnl_label') : t('tw_total'), `${fmt(convert(parseFloat(preis)), locale)} ${symbol}`] : null,
          stueck ? [t('tw_unit_price'), `${fmt(convert(parseFloat(stueck)), locale)} ${symbol}`] : null,
          gebuehr && parseFloat(gebuehr) > 0 ? [t('tw_fee'), `${fmt(convert(parseFloat(gebuehr)), locale)} ${symbol}`] : null,
          [t('tw_exchange'), exchange],
          hash ? [t('tw_txhash'), hash.length > 20 ? hash.slice(0, 20) + '…' : hash] : null,
          notiz ? [t('tw_note'), notiz] : null,
        ].filter(Boolean).map(([k, v], i, arr) => (
          <div key={k} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
            padding: '12px 2px', borderTop: `1px solid ${T.line}`,
            borderBottom: i === arr.length - 1 ? `1px solid ${T.line}` : 'none',
          }}>
            <span style={{ fontSize: 12, color: T.sub, fontFamily: F.family }}>{k}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: F.family, textAlign: 'right' }}>{v}</span>
          </div>
        ))}
      </div>

      <button onClick={onSave} style={mkBtnPrimary(T)}>
        ✓ {t('tw_save_x', { x: typInfo ? t(typInfo.labelKey) : t('tw_transaction') })}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// FIAT-DETAILS — Bank ↔ Börse (EUR). Kein Coin/Quelle: nur Datum, Betrag (€),
// Börse, optional Gebühr + Notiz. Speichert direkt (kein Confirm-Step nötig).
// ═══════════════════════════════════════════════════════════════════════════════
function FiatDetailsStep({ typ, datum, setDatum, menge, setMenge, gebuehr, setGebuehr, exchange, setExchange, notiz, setNotiz, onSave }) {
  const { T } = useTheme();
  const { t } = useT();
  const isIn = typ === 'fiat_in';
  const canSave = !!datum && menge !== '' && parseFloat(menge) > 0;
  const accent = isIn ? '#4FAEC0' : '#D488A8';
  return (
    <div>
      <StepHero
        title={isIn ? t('tw_type_fiat_in') : t('tw_type_fiat_out')}
        sub={isIn ? t('tw_fiat_in_sub') : t('tw_fiat_out_sub')}
        icon={(
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d={FIAT_BANKNOTE} />
          </svg>
        )}
      />
      <div style={fieldGroupStyle(T)}>
        <FieldRow label={t('date')} type="date" value={datum} onChange={setDatum} />
        <FieldNum label={`${t('amount')} (EUR)`} placeholder="0.00" value={menge} onChange={setMenge} />
        <FieldExchange label={t('tw_exchange')} value={exchange} onChange={(lbl) => setExchange(lbl)} />
        <FieldNum label={`${t('tw_fee')} (EUR)`} placeholder="0.00" value={gebuehr} onChange={setGebuehr} />
        <FieldRow label={t('tw_note_opt')} value={notiz} onChange={setNotiz} placeholder={isIn ? 'Bank → Börse' : 'Börse → Bank'} />
      </div>
      <button onClick={onSave} disabled={!canSave} style={canSave ? mkBtnPrimary(T) : mkBtnDisabled(T)}>
        ✓ {isIn ? t('tw_type_fiat_in') : t('tw_type_fiat_out')}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ORCHESTRATOR — State-Maschine, routet zwischen Steps
// ═══════════════════════════════════════════════════════════════════════════════
export default function TransactionWizard({
  marketData, existingTxs, onSave, onMethodSelect,
  initialMethod, onStepChange,
}) {
  const [step, setStep] = useState(initialMethod ? 1 : 0);
  const [typ, setTyp] = useState('kauf');
  const [wizardMarket, setWizardMarket] = useState({});

  // sourceMode: 'own' | 'external' | 'gift' — bei Transfers vor Details
  const [sourceMode, setSourceMode] = useState(null);

  useEffect(() => {
    const ids = COIN_LIST.map(c => c.id).filter(Boolean);
    fetchMarketData(ids).then(data => setWizardMarket(data));
  }, []);

  const allMarketData = useMemo(() => ({ ...wizardMarket, ...marketData }), [wizardMarket, marketData]);

  const [selectedCoin, setSelectedCoin] = useState(null);
  const [menge, setMenge] = useState('');
  const [datum, setDatum] = useState(new Date().toISOString().split('T')[0]);
  const [preis, setPreis] = useState('');
  const [gebuehr, setGebuehr] = useState('');
  const [hash, setHash] = useState('');
  const [exchange, setExchange] = useState('Binance');
  const [wallet, setWallet] = useState(''); // Ziel bei „über Dienstleister"-Käufen (MoonPay → Ledger)
  const [notiz, setNotiz] = useState('');

  // Swap-Felder
  const [swapCoinOut, setSwapCoinOut] = useState(null);
  const [swapMengeOut, setSwapMengeOut] = useState('');
  const [swapCoinIn, setSwapCoinIn] = useState(null);
  const [swapMengeIn, setSwapMengeIn] = useState('');

  const isSwap     = typ === 'swap';
  const isTransfer = typ === 'einzahlung' || typ === 'auszahlung';
  const isBuy      = typ === 'kauf';
  const isFiat     = typ === 'fiat_in' || typ === 'fiat_out'; // Bank ↔ Börse (EUR)
  const isIncome   = typ === 'staking' || typ === 'interest' || typ === 'airdrop';
  const isLoss     = typ === 'loss';
  const isDerivative = typ === 'futures_pnl' || typ === 'funding_fee';

  // ── Step-Routing-Tabelle ──────────────────────────────────────────────────
  // Verkauf/Income/Loss: 0=Method → 1=Type → 2=Coin → 3=Details → 4=Confirm
  // Kauf:                 0 → 1 → 2=Coin → 3=WoGekauft → 4=Details → 5=Confirm
  // Swap:                 0=Method → 1=Type → 2=SwapDetails → 3=Confirm
  // Transfer eigene Wallet: 0 → 1 → 2=Coin → 3=Source → 4=TransferDetails → 5=TransferConfirm
  // Transfer extern:        0 → 1 → 2=Coin → 3=Source → 4=TransferDetails → 5=Confirm
  // Transfer geschenk:      0 → 1 → 2=Coin → 3=Source → 4=Details (typ überschrieben) → 5=Confirm

  // Fiat: 0=Method → 1=Type → 2=FiatDetails (mit Speichern). Kurz & ohne Coin/Quelle.
  const totalSteps = isFiat ? 3 : isSwap ? 4 : (isTransfer || isBuy) ? 6 : 5;

  // Bei sourceMode='gift' → effektiver typ ist airdrop (einz.) bzw. verkauf (ausz.)
  const effectiveTyp = (isTransfer && sourceMode === 'gift')
    ? (typ === 'einzahlung' ? 'airdrop' : 'verkauf')
    : typ;

  // ── goBack: kontextsensitiv je nach Flow ──────────────────────────────────
  // Standard:  0 ← 1 ← 2 ← 3 ← 4
  // Swap:      0 ← 1 ← 2(SwapDetails) ← 3(Confirm)
  // Transfer:  0 ← 1 ← 2(Coin) ← 3(Source) ← 4(Details) ← 5(Confirm)
  const goBack = () => {
    if (step <= 0) return;
    setStep(s => s - 1);
  };

  useEffect(() => {
    onStepChange?.(step, totalSteps, step > 0 ? goBack : null);
  }, [step, totalSteps]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMethodSelect = (m) => {
    if (m === 'csv' || m === 'api' || m === 'wallet' || m === 'utxowallet') {
      onMethodSelect?.(m);
      return;
    }
    setStep(1);
  };

  const handleSave = (linkInfo, isExternal) => {
    if (isSwap) {
      onSave({
        id: Date.now(), typ: 'swap',
        datum,
        coinOut: swapCoinOut.sym, coinIdOut: swapCoinOut.id, mengeOut: parseFloat(swapMengeOut),
        coinIn:  swapCoinIn.sym,  coinIdIn:  swapCoinIn.id,  mengeIn:  parseFloat(swapMengeIn),
        preis: parseFloat(preis),
        gebuehr: gebuehr ? parseFloat(gebuehr) : 0,
        hash, exchange, notiz,
      });
      return;
    }

    // Fiat-Geld (Bank ↔ Börse): bestandsneutrale einzahlung/auszahlung mit coin='EUR'
    // (preis = menge = EUR-Wert), wie buildFiatTx. Wird nie auto-gepaart.
    if (isFiat) {
      const m = parseFloat(menge) || 0;
      onSave({
        id: Date.now(),
        typ: typ === 'fiat_in' ? 'einzahlung' : 'auszahlung',
        coin: 'EUR', coinId: null,
        datum, menge: m, preis: m, stueck: 0,
        gebuehr: gebuehr ? parseFloat(gebuehr) : 0,
        hash: '', exchange, notiz, source: 'manual',
      });
      return;
    }

    // Transfer mit Geschenk → als airdrop/verkauf speichern
    if (isTransfer && sourceMode === 'gift') {
      const p = preis ? parseFloat(preis) : 0;
      const m = parseFloat(menge);
      onSave({
        id: Date.now(),
        typ: typ === 'einzahlung' ? 'airdrop' : 'verkauf',
        coin: selectedCoin.sym, coinId: selectedCoin.id,
        datum, menge: m, preis: p,
        stueck: typ === 'auszahlung' && m > 0 ? p / m : 0,
        gebuehr: gebuehr ? parseFloat(gebuehr) : 0,
        hash, exchange, notiz, source: 'manual',
        giftFlow: true,
      });
      return;
    }

    if (isTransfer) {
      onSave({
        id: Date.now(), typ,
        coin: selectedCoin.sym, coinId: selectedCoin.id,
        datum, menge: parseFloat(menge), preis: 0, stueck: 0,
        gebuehr: gebuehr ? parseFloat(gebuehr) : 0,
        hash, exchange, notiz, source: 'manual',
      }, linkInfo ?? null, isExternal ?? (sourceMode === 'external'));
      return;
    }

    if (isIncome) {
      const p = preis ? parseFloat(preis) : 0;
      onSave({
        id: Date.now(), typ,
        coin: selectedCoin.sym, coinId: selectedCoin.id,
        datum, menge: parseFloat(menge), preis: p, stueck: 0,
        gebuehr: gebuehr ? parseFloat(gebuehr) : 0,
        hash, exchange, notiz, source: 'manual',
      });
      return;
    }

    if (isLoss) {
      const m = parseFloat(menge);
      onSave({
        id: Date.now(), typ: 'loss',
        coin: selectedCoin.sym, coinId: selectedCoin.id,
        datum, menge: m, preis: 0, stueck: 0,
        gebuehr: gebuehr ? parseFloat(gebuehr) : 0,
        hash, exchange, notiz, source: 'manual',
      });
      return;
    }

    if (isDerivative) {
      // Futures PnL / Funding-Fee = reine €-Buchung (kein Lot-Effekt). `preis` ist
      // der SIGNIERTE EUR-Betrag (negativ = Verlust/Kosten) — exakt wie der Import
      // ihn ablegt (canonicalNormalizer lässt preis bei diesen Typen vorzeichen-
      // behaftet). menge = 0 (settlement-Coin nur fürs Display/Börsen-Zuordnung).
      onSave({
        id: Date.now(), typ,
        coin: selectedCoin.sym, coinId: selectedCoin.id,
        datum, menge: 0, preis: parseFloat(preis) || 0, stueck: 0,
        gebuehr: gebuehr ? parseFloat(gebuehr) : 0,
        hash, exchange, notiz, source: 'manual',
      });
      return;
    }

    // Kauf / Verkauf
    const m = parseFloat(menge), p = parseFloat(preis);
    onSave({
      id: Date.now(), typ, coin: selectedCoin.sym, coinId: selectedCoin.id,
      datum, menge: m, preis: p, stueck: m > 0 ? p / m : 0,
      gebuehr: gebuehr ? parseFloat(gebuehr) : 0,
      hash, exchange, notiz, source: 'manual',
      // Ziel-Wallet (MoonPay → Ledger) nur bei Kauf.
      ...(typ === 'kauf' && wallet ? { wallet } : {}),
    });
  };

  // ── Step-Rendering ────────────────────────────────────────────────────────
  // In StepMotion gewrappt: jeder Step-Wechsel blendet per Spring ein (App-weit).
  // KEIN typ im Key: sonst re-mountet/re-animiert der ganze Step bei JEDER
  // Typ-Auswahl im TypeStep → sichtbares Zucken. Re-Animation nur bei echtem
  // Step-Wechsel (step) bzw. Quellen-Untermodus (sourceMode).
  const stepKey = `${step}:${sourceMode || ''}`;
  const renderStep = () => {
  if (step === 0) {
    return <MethodStep onSelect={handleMethodSelect} />;
  }

  if (step === 1) {
    return (
      <TypeStep
        typ={typ}
        setTyp={(t) => { setTyp(t); setSourceMode(null); }}
        onNext={() => setStep(2)}
      />
    );
  }

  if (isSwap) {
    if (step === 2) {
      return (
        <SwapDetailsStep
          marketData={allMarketData}
          datum={datum} setDatum={setDatum}
          coinOut={swapCoinOut} setCoinOut={setSwapCoinOut}
          mengeOut={swapMengeOut} setMengeOut={setSwapMengeOut}
          coinIn={swapCoinIn} setCoinIn={setSwapCoinIn}
          mengeIn={swapMengeIn} setMengeIn={setSwapMengeIn}
          preis={preis} setPreis={setPreis}
          gebuehr={gebuehr} setGebuehr={setGebuehr}
          exchange={exchange} setExchange={setExchange}
          hash={hash} setHash={setHash}
          notiz={notiz} setNotiz={setNotiz}
          onNext={() => setStep(3)}
        />
      );
    }
    return (
      <ConfirmStep
        typ={typ} marketData={allMarketData}
        datum={datum} menge={menge} preis={preis} gebuehr={gebuehr}
        hash={hash} exchange={exchange} notiz={notiz}
        isSwap swapCoinOut={swapCoinOut} swapMengeOut={swapMengeOut}
        swapCoinIn={swapCoinIn} swapMengeIn={swapMengeIn}
        onSave={() => handleSave()}
      />
    );
  }

  // Fiat-Geld (Bank ↔ Börse): kein Coin-/Quelle-Schritt — direkt Betrag + Börse.
  if (isFiat) {
    return (
      <FiatDetailsStep
        typ={typ}
        datum={datum} setDatum={setDatum}
        menge={menge} setMenge={setMenge}
        gebuehr={gebuehr} setGebuehr={setGebuehr}
        exchange={exchange} setExchange={setExchange}
        notiz={notiz} setNotiz={setNotiz}
        onSave={() => handleSave()}
      />
    );
  }

  // Coin-Step (alle non-swap-Typen)
  if (step === 2) {
    return (
      <CoinStep
        typ={typ}
        selectedCoin={selectedCoin}
        setSelectedCoin={setSelectedCoin}
        marketData={allMarketData}
        onNext={() => setStep(3)}
      />
    );
  }

  // Transfer-Flow mit SourceStep
  if (isTransfer) {
    if (step === 3) {
      return (
        <SourceStep
          typ={typ}
          selectedCoin={selectedCoin}
          marketData={allMarketData}
          onSelect={(mode) => { setSourceMode(mode); setStep(4); }}
        />
      );
    }
    if (step === 4) {
      // gift → Details-Form (mit Preis), sonst Transfer-Details
      if (sourceMode === 'gift') {
        return (
          <DetailsStep
            typ={effectiveTyp}
            selectedCoin={selectedCoin}
            marketData={allMarketData}
            datum={datum} setDatum={setDatum}
            menge={menge} setMenge={setMenge}
            preis={preis} setPreis={setPreis}
            gebuehr={gebuehr} setGebuehr={setGebuehr}
            hash={hash} setHash={setHash}
            exchange={exchange} setExchange={setExchange}
            wallet={wallet} setWallet={setWallet}
            notiz={notiz} setNotiz={setNotiz}
            onNext={() => setStep(5)}
          />
        );
      }
      return (
        <TransferDetailsStep
          typ={typ}
          selectedCoin={selectedCoin}
          marketData={allMarketData}
          sourceMode={sourceMode}
          datum={datum} setDatum={setDatum}
          menge={menge} setMenge={setMenge}
          gebuehr={gebuehr} setGebuehr={setGebuehr}
          hash={hash} setHash={setHash}
          exchange={exchange} setExchange={setExchange}
          notiz={notiz} setNotiz={setNotiz}
          onNext={() => setStep(5)}
        />
      );
    }
    // step 5: Confirm
    if (sourceMode === 'own') {
      return (
        <TransferConfirmStep
          typ={typ} selectedCoin={selectedCoin} marketData={allMarketData}
          datum={datum} menge={menge} gebuehr={gebuehr}
          exchange={exchange} hash={hash} notiz={notiz}
          existingTxs={existingTxs}
          onSave={(linkInfo, ext) => handleSave(linkInfo, ext)}
        />
      );
    }
    // external oder gift → simpler Confirm
    return (
      <ConfirmStep
        typ={effectiveTyp} selectedCoin={selectedCoin} marketData={allMarketData}
        datum={datum} menge={menge} preis={preis} gebuehr={gebuehr}
        hash={hash} exchange={exchange} notiz={notiz}
        sourceMode={sourceMode}
        onSave={() => handleSave(null, sourceMode === 'external')}
      />
    );
  }

  // Kauf bekommt einen eigenen „Wo gekauft?"-Schritt (Bild 2) zwischen Coin und
  // Formular — Direkt auf der Börse vs. Über Zahlungsdienstleister (MoonPay → Ledger).
  if (isBuy) {
    if (step === 3) {
      return (
        <BuySourceStep
          selectedCoin={selectedCoin}
          marketData={allMarketData}
          setExchange={setExchange}
          setWallet={setWallet}
          onNext={() => setStep(4)}
        />
      );
    }
    if (step === 4) {
      return (
        <DetailsStep
          typ={typ}
          selectedCoin={selectedCoin}
          marketData={allMarketData}
          datum={datum} setDatum={setDatum}
          menge={menge} setMenge={setMenge}
          preis={preis} setPreis={setPreis}
          gebuehr={gebuehr} setGebuehr={setGebuehr}
          hash={hash} setHash={setHash}
          exchange={exchange} setExchange={setExchange}
          wallet={wallet} setWallet={setWallet}
          notiz={notiz} setNotiz={setNotiz}
          onNext={() => setStep(5)}
        />
      );
    }
    return (
      <ConfirmStep
        typ={typ} selectedCoin={selectedCoin} marketData={allMarketData}
        datum={datum} menge={menge} preis={preis} gebuehr={gebuehr}
        hash={hash} exchange={exchange} notiz={notiz}
        onSave={() => handleSave()}
      />
    );
  }

  // Übrige Standard-Typen (Verkauf/Income/Loss): 3=Details → 4=Confirm
  if (step === 3) {
    return (
      <DetailsStep
        typ={typ}
        selectedCoin={selectedCoin}
        marketData={allMarketData}
        datum={datum} setDatum={setDatum}
        menge={menge} setMenge={setMenge}
        preis={preis} setPreis={setPreis}
        gebuehr={gebuehr} setGebuehr={setGebuehr}
        hash={hash} setHash={setHash}
        exchange={exchange} setExchange={setExchange}
        wallet={wallet} setWallet={setWallet}
        notiz={notiz} setNotiz={setNotiz}
        onNext={() => setStep(4)}
      />
    );
  }

  return (
    <ConfirmStep
      typ={typ} selectedCoin={selectedCoin} marketData={allMarketData}
      datum={datum} menge={menge} preis={preis} gebuehr={gebuehr}
      hash={hash} exchange={exchange} notiz={notiz}
      onSave={() => handleSave()}
    />
  );
  };

  return <StepMotion k={stepKey}>{renderStep()}</StepMotion>;
}

// ═══════════════════════════════════════════════════════════════════════════════
// TRANSACTION EDIT SCREEN — bleibt als eigene Komponente, neue Field-Primitives
// ═══════════════════════════════════════════════════════════════════════════════
// Bearbeiten nutzt exakt dieselbe Liste/Reihenfolge wie Erstellen (Single Source).
const EDIT_TYPES = WIZARD_TYPES;

const NEEDS_PRICE  = new Set(['kauf', 'verkauf']);
const ALLOWS_PRICE = new Set(['kauf', 'verkauf', 'staking', 'interest', 'airdrop']);
// Derivat-Typen haben keine "Wallet-Empfaenger"-Frage und kein Sourcemode
const DERIVATIVE_TYPES = new Set(['futures_pnl', 'funding_fee']);

// Formatiert Coin-Mengen mit sinnvoller Stellenzahl (kleine Werte → mehr Decimals).
function fmtCoinAmount(n) {
  if (!(n > 0)) return '0';
  if (n >= 1)    return n.toFixed(4).replace(/\.?0+$/, '');
  if (n >= 0.01) return n.toFixed(6).replace(/\.?0+$/, '');
  return n.toFixed(8).replace(/\.?0+$/, '');
}

// Zeigt erkannte Gebuehren-Metadaten an, wenn die Gebuehr beim Import
// bereits in die Menge eingerechnet wurde (statt im gebuehr-Feld zu landen).
// Verhindert User-Verwirrung "warum steht da keine Gebuehr obwohl ich eine
// bezahlt habe?". Read-only — User darf trotzdem manuell was nachtragen.
function FeeInfoBanner({ tx }) {
  const { T } = useTheme();
  const { t } = useT();
  const fee = tx?._fee;
  if (!fee || !(fee.amount > 0) || fee.absorbed === 'gebuehr') return null;
  const amt = `${fmtCoinAmount(fee.amount)} ${fee.coin}`;
  const text = fee.absorbed === 'menge_reduced'
    ? t('tw_fee_detected_reduced', { amt })
    : fee.absorbed === 'menge_increased'
      ? t('tw_fee_detected_increased', { amt })
      : t('tw_fee_detected_separate', { amt });
  return (
    <div style={{
      padding: '8px 12px',
      background: T.glass,
      border: `1px solid ${T.glassBorder}`,
      borderRadius: mr(16),
      fontSize: 11.5,
      color: T.sub,
      fontFamily: F.family,
      lineHeight: 1.4,
    }}>
      ℹ {text}
    </div>
  );
}

// ── Verbindungs-Sektion (Edit-Maske) ─────────────────────────────────────────
// Verbinden / trennen / fehlende Gegenbuchung anlegen — direkt in der Edit-Maske.
// Der Listen-„Verbinden-Modus" existiert nur mobil + nur bei offenen Transfers;
// diese Sektion macht dasselbe auf Web UND Mobil, genau dort wo man „Bearbeiten"
// drückt. Zustand 3 („Gegenbuchung anlegen") löst den Fall, dass die Gegenseite
// gar nicht (mehr) existiert — z.B. weil Ledger ein 0-Saldo-Asset nicht mehr
// exportiert und der Re-Import die alte Auszahlung verlor.
function TransferConnectionSection({ tx, txs, marketData, onLink, onUnlink, onCreateCounterpart }) {
  const { T } = useTheme();
  const { t } = useT();
  const idOf = (x) => x._docId || String(x.id);
  const selfId = idOf(tx);
  const isOut = tx.typ === 'auszahlung';

  const partner = useMemo(() => {
    if (!tx.linkedTxId) return null;
    return (txs || []).find(x => idOf(x) === tx.linkedTxId) || null;
  }, [tx.linkedTxId, txs]); // eslint-disable-line react-hooks/exhaustive-deps

  const candidates = useMemo(() => {
    if (partner) return [];
    const out = [];
    for (const o of (txs || [])) {
      if (idOf(o) === selfId) continue;
      if (o.typ !== 'einzahlung' && o.typ !== 'auszahlung') continue;
      if (o.linkedTxId) continue;
      const ev = evaluateTransferPair(tx, o);
      if (!ev.valid || ev.blockers.length) continue;
      out.push({ tx: o, score: ev.score });
    }
    return out.sort((a, b) => b.score - a.score).slice(0, 4);
  }, [tx, txs, partner, selfId]);

  const [showCreate, setShowCreate] = useState(false);
  const [cpExchange, setCpExchange] = useState(isOut ? 'Externe Wallet' : (tx.wallet || 'Ledger'));
  const [busy, setBusy] = useState(false);

  const run = async (fn) => {
    if (busy) return;
    setBusy(true);
    try { await fn(); } catch (_) { /* Toast kommt aus dem Handler */ } finally { setBusy(false); }
  };
  const doLink = (cand) => run(async () => {
    const ev = evaluateTransferPair(tx, cand.tx);
    if (ev.valid && ev.blockers.length === 0) await onLink(ev.out, ev.in);
  });
  const doUnlink = () => run(() => onUnlink(tx, partner));
  const doCreate = () => run(() => onCreateCounterpart(tx, { exchange: cpExchange }));

  const label = (x) =>
    `${x.exchange || '—'} · ${x.datum || ''} · ${fmtCoinAmount(Number(x.menge) || 0)} ${x.coin || x.sym || ''}`;
  const rowCard = {
    padding: '11px 13px', borderRadius: mr(12),
    background: T.surface, border: `1px solid ${T.line || T.border}`,
    display: 'flex', alignItems: 'center', gap: 10,
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ ...mkFieldLabel(T), marginBottom: 10 }}>{t('conn_title')}</div>

      {partner ? (
        <div style={rowCard}>
          <CoinImage sym={partner.coin || partner.sym} marketData={marketData} size={28} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.mute, marginBottom: 2 }}>{t('conn_linked_to')}</div>
            <div style={{ fontSize: 12.5, color: T.text, fontFamily: F.family, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label(partner)}</div>
          </div>
          <button onClick={doUnlink} disabled={busy} style={{
            padding: '7px 12px', borderRadius: mr(10), cursor: busy ? 'default' : 'pointer',
            background: 'transparent', border: `1px solid ${T.line || T.border}`,
            color: T.sub, fontFamily: F.family, fontSize: 12, fontWeight: 600, flexShrink: 0,
          }}>{busy ? t('conn_busy') : t('conn_unlink')}</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {candidates.length > 0 && (
            <div>
              <div style={{ fontSize: 11, color: T.mute, fontFamily: F.family, marginBottom: 6 }}>{t('conn_suggestions')}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {candidates.map(c => (
                  <div key={idOf(c.tx)} style={rowCard}>
                    <CoinImage sym={c.tx.coin || c.tx.sym} marketData={marketData} size={24} />
                    <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: T.text, fontFamily: F.family, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label(c.tx)}</div>
                    <button onClick={() => doLink(c)} disabled={busy} style={{
                      padding: '7px 12px', borderRadius: mr(10), cursor: busy ? 'default' : 'pointer',
                      background: T.signal || T.red, border: 'none', color: '#fff',
                      fontFamily: F.family, fontSize: 12, fontWeight: 700, flexShrink: 0,
                    }}>{t('conn_link')}</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!showCreate ? (
            <button onClick={() => setShowCreate(true)} disabled={busy} style={{
              padding: '11px', borderRadius: mr(12), cursor: 'pointer',
              background: 'transparent', border: `1px dashed ${T.line || T.border}`,
              color: T.sub, fontFamily: F.family, fontSize: 12.5, fontWeight: 600,
            }}>+ {t('conn_create')}</button>
          ) : (
            <div style={{
              padding: '12px 14px', borderRadius: mr(12),
              background: T.surface, border: `1px solid ${T.line || T.border}`,
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <div style={{ fontSize: 11.5, color: T.sub, fontFamily: F.family, lineHeight: 1.45 }}>{t('conn_none_hint')}</div>
              <div style={{ fontSize: 11.5, color: T.mute, fontFamily: F.family }}>{isOut ? t('conn_create_in') : t('conn_create_out')}</div>
              <FieldSelect label={t('conn_create_exchange')} value={cpExchange} onChange={setCpExchange} options={EXCHANGES} withExchangeLogo />
              <button onClick={doCreate} disabled={busy} style={{
                padding: '11px', borderRadius: mr(12), cursor: busy ? 'default' : 'pointer',
                background: T.signal || T.red, border: 'none', color: '#fff',
                fontFamily: F.family, fontSize: 13, fontWeight: 700,
              }}>{busy ? t('conn_busy') : `⛓ ${t('conn_create_confirm')}`}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Float-Rauschen aus gespeicherten Zahlen entfernen (z.B. 3.3899999999999997 → "3.39"),
// OHNE echte Nachkommastellen zu verlieren. toPrecision(15) liegt unter Floats
// ~15-17 signifikanten Stellen, schneidet also nur die Rundungs-Artefakte ab.
// Für die Initialwerte der Edit-Felder (sonst leakt der rohe Float ins Eingabefeld).
function cleanNumStr(v) {
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (!isFinite(n) || n === 0) return '';
  return String(Number(n.toPrecision(15)));
}

export function TransactionEditScreen({ tx, txs, marketData, onSave, onBack, onLink, onUnlink, onCreateCounterpart }) {
  const { T } = useTheme();
  const { t } = useT();
  const { convert, symbol, locale } = useCurrency();
  const { isWide } = useBreakpoint();
  const isSwap = tx.typ === 'swap';

  const [typ,      setTyp]      = useState(tx.typ);
  const [datum,    setDatum]    = useState(tx.datum);
  // Coin als State: wird gesetzt, wenn ein Tausch in einen Einzel-Asset-Typ
  // umklassifiziert wird (dann = erhaltener Coin). Sonst = tx.coin.
  const [coin,     setCoin]     = useState(tx.coin || null);
  const meta  = coin ? COIN_MAP[coin] : null;
  const mData = meta ? marketData?.[meta.id] : null;
  const [menge,    setMenge]    = useState(isSwap ? '' : cleanNumStr(tx.menge));
  const [preis,    setPreis]    = useState(cleanNumStr(tx.preis));
  const [gebuehr,  setGebuehr]  = useState(cleanNumStr(tx.gebuehr));
  const [exchange, setExchange] = useState(tx.exchange || 'Binance');
  const [notiz,    setNotiz]    = useState(tx.notiz || '');
  // Ziel bei „über Dienstleister"-Käufen (z.B. MoonPay → Ledger). Setzt der
  // Exchange-Picker im Dienstleister-Pfad; leer = direkt auf der Börse.
  const [wallet,   setWallet]   = useState(tx.wallet || '');

  // SourceMode für Transfers — gleich wie im Wizard SourceStep.
  // 'own'      = eigene Wallet (wird ggf. mit Gegenstück gematcht)
  // 'external' = Freund/extern (kein Match erwartet, externalTransfer=true)
  // 'gift'     = Geschenk/Bezahlung → typ wechselt auf airdrop bzw. verkauf
  // Initial-Default aus der EFFEKTIVEN Klassifizierung ableiten (nicht nur aus dem
  // gespeicherten Flag) — sonst zeigte der Editor „eigene Wallet" obwohl die Liste die
  // TX als „gesendet/empfangen (extern)" darstellt (externalTransfer wird bei einseitigen
  // Sends nie gesetzt → Default fiel auf 'own', Extern-Häkchen fehlte). Reihenfolge:
  //  • externalTransfer-Flag gesetzt → 'external'
  //  • bewiesener Eigen-Transfer (ownWalletTransfer) oder gematchtes/verlinktes Paar → 'own'
  //  • sonst ungematchter Ein-/Ausgang ohne Gegenpart = in der Liste „gesendet/empfangen"
  //    (extern) → Editor matcht diese Anzeige → 'external' vorbelegt.
  const [sourceMode, setSourceMode] = useState(() => {
    if (tx.externalTransfer) return 'external';
    if (tx.ownWalletTransfer || tx.linkedTxId || tx.transferGroupId) return 'own';
    if (tx.typ === 'auszahlung' || tx.typ === 'einzahlung') return 'external';
    return 'own';
  });

  // Fiat-Geld (Bank ↔ Börse): einzahlung/auszahlung MIT Fiat-Coin (EUR/USD/…).
  // Bekommt einen eigenen, sauberen Edit-Pfad (Richtung + Betrag + Börse) statt
  // des Coin-Transfer-Formulars — sonst würde der generelle Save den EUR-Wert
  // (preis) auf 0 setzen. Engine-typ bleibt einzahlung/auszahlung = bestandsneutral.
  const isFiatTx = (tx.typ === 'einzahlung' || tx.typ === 'auszahlung') && isFiatCoin(tx.coin);
  const [fiatDir, setFiatDir] = useState(tx.typ === 'auszahlung' ? 'out' : 'in');

  const [mengeOut, setMengeOut] = useState(isSwap ? cleanNumStr(tx.mengeOut) : '');
  const [mengeIn,  setMengeIn]  = useState(isSwap ? cleanNumStr(tx.mengeIn)  : '');
  const [fetchingPrice, setFetchingPrice] = useState(false);
  const [priceErr, setPriceErr] = useState(false);

  // Tausch-Umwandlung: eine Nicht-Swap-TX (z.B. ein importierter „Gesendet"-
  // Eintrag, der eigentlich ein DEX-Swap war) lässt sich im Bearbeiten-Dialog zu
  // einem Tausch umklassifizieren. Der abgegebene Coin ist der bestehende tx.coin;
  // der ERHALTENE Coin wird hier gewählt. (Schon-Swaps laufen über den isSwap-Pfad.)
  const editingAsSwap = typ === 'swap';
  const [swapCoinIn, setSwapCoinIn] = useState(null);

  // TX-Hash: vorhandener Hash bleibt gesperrt (read-only) + kopierbar; fehlt
  // einer, kann er hier nachgetragen werden (Konsistenz Wallet-/Asset-Edit).
  const [hash, setHash] = useState(tx.hash || '');
  const [copied, setCopied] = useState(false);
  const copyHash = () => {
    try {
      navigator.clipboard?.writeText(tx.hash || hash || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* clipboard nicht verfügbar */ }
  };

  const fetchHistoricalPrice = async () => {
    // Wert aus historischem Marktkurs ableiten. WICHTIG: `sym` mitgeben — der
    // Proxy braucht ihn für den CryptoCompare/Binance-Fallback bei Tagen >365
    // (CoinGecko-Demo deckt nur 365 Tage). Swap (z.B. Binance-CSV „Small Assets
    // Exchange", ohne €-Wert/Hash): zuerst abgegebene Seite (coinOut × Menge_out);
    // wenn dort kein Kurs kommt, erhaltene Seite (coinIn × Menge_in) — beide
    // geben ~denselben €-Wert. Sonst: tx.coin × Menge.
    if (!datum) return;
    const tryOne = async (id, sym, amt) => {
      const a = parseFloat(amt);
      if (!id || !sym || !(a > 0)) return null;
      const unit = await getHistoricalPriceAt(id, datum, 'eur', sym);
      return (typeof unit === 'number' && unit > 0) ? Math.round(a * unit * 100) / 100 : null;
    };
    setFetchingPrice(true); setPriceErr(false);
    try {
      let value = null;
      if (isSwap) {
        value = await tryOne(tx.coinIdOut || COIN_MAP[tx.coinOut]?.id, tx.coinOut, mengeOut);
        if (value == null) value = await tryOne(tx.coinIdIn || COIN_MAP[tx.coinIn]?.id, tx.coinIn, mengeIn);
      } else {
        value = await tryOne(tx.coinId || COIN_MAP[tx.coin]?.id, tx.coin, menge);
      }
      if (value != null) setPreis(String(value));
      else setPriceErr(true);
    } catch {
      setPriceErr(true);
    } finally { setFetchingPrice(false); }
  };

  // Wenn preis=0 beim Öffnen: historischen Kurs automatisch nachladen (einmalig).
  useEffect(() => {
    const p = parseFloat(preis);
    if ((isNaN(p) || p === 0) && datum) {
      const hasAmt = isSwap
        ? (parseFloat(mengeOut) > 0 || parseFloat(mengeIn) > 0)
        : parseFloat(menge) > 0;
      if (hasAmt) fetchHistoricalPrice();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTypChange = (newTyp) => {
    setTyp(newTyp);
    // Tausch → Einzel-Asset-Typ umklassifizieren: den ERHALTENEN Coin (coinIn) als
    // Asset übernehmen, erhaltene Menge als Menge. Der Nutzer korrigiert damit eine
    // Fehl-Klassifizierung; die Engine rechnet aus der gespeicherten TX neu.
    if (isSwap && newTyp !== 'swap') {
      if (!coin) setCoin(tx.coinIn || tx.coinOut || null);
      if (!menge || parseFloat(menge) === 0) setMenge(cleanNumStr(tx.mengeIn || tx.mengeOut));
    }
    // Tausch: der Wert-Feld (preis) hält den EUR-Wert des Tauschs. Vorbelegen aus
    // countervalue, falls vorhanden; nicht leeren.
    if (newTyp === 'swap') {
      if ((!preis || parseFloat(preis) === 0) && tx.countervalue) setPreis(String(tx.countervalue));
      setSourceMode('own');
      return;
    }
    if (NEEDS_PRICE.has(newTyp) && (!preis || parseFloat(preis) === 0) && tx.countervalue) {
      setPreis(String(tx.countervalue));
    }
    if (!ALLOWS_PRICE.has(newTyp)) setPreis('');
    // SourceMode nur sinnvoll für Transfers — sonst zurücksetzen.
    // Derivate (futures_pnl, funding_fee) haben kein Wallet-Mapping.
    const isTransferType = newTyp === 'einzahlung' || newTyp === 'auszahlung';
    if (!isTransferType || DERIVATIVE_TYPES.has(newTyp)) {
      setSourceMode('own');
    }
  };

  // 'gift' im Edit-Form bedeutet: Typ-Wechsel auf airdrop (für einzahlung)
  // bzw. verkauf (für auszahlung) — analog zum Wizard-Verhalten.
  const handleSourceModeChange = (mode) => {
    setSourceMode(mode);
    if (mode === 'gift') {
      if (typ === 'einzahlung') handleTypChange('airdrop');
      else if (typ === 'auszahlung') handleTypChange('verkauf');
    }
  };

  const typMeta     = EDIT_TYPES.find(x => x.id === typ) || EDIT_TYPES[0];
  const needsPrice  = NEEDS_PRICE.has(typ);
  const allowsPrice = ALLOWS_PRICE.has(typ);
  const isDeriv     = DERIVATIVE_TYPES.has(typ);

  const stueck = needsPrice && menge && preis && parseFloat(menge) > 0 && parseFloat(preis) > 0
    ? (parseFloat(preis) / parseFloat(menge)).toFixed(6)
    : null;

  const canSave = (isSwap && editingAsSwap)
    ? (mengeOut && mengeIn && preis && parseFloat(mengeOut) > 0 && parseFloat(mengeIn) > 0 && parseFloat(preis) > 0)
    : editingAsSwap
      ? (menge && parseFloat(menge) > 0 && swapCoinIn && mengeIn && parseFloat(mengeIn) > 0 && preis && parseFloat(preis) > 0)
      : isDeriv
        // Derivat: nur der signierte €-PnL ist Pflicht (menge irrelevant).
        ? (preis !== '' && Number.isFinite(parseFloat(preis)))
        : (menge && parseFloat(menge) > 0 && (!needsPrice || (preis && parseFloat(preis) > 0)));

  const handleSave = () => {
    if (isSwap && editingAsSwap) {
      onSave({ ...tx, datum, mengeOut: parseFloat(mengeOut), mengeIn: parseFloat(mengeIn), preis: parseFloat(preis), gebuehr: parseFloat(gebuehr) || 0, exchange, notiz });
    } else if (editingAsSwap) {
      // Nicht-Swap → Tausch umklassifizieren: bestehender Coin = abgegeben,
      // gewählter Coin = erhalten. menge ist die abgegebene Menge.
      const outSym = tx.coin;
      onSave({
        ...tx,
        typ: 'swap',
        coin: null,
        sym: null,
        menge: null,
        coinOut:   outSym,
        coinIdOut: tx.coinId || COIN_MAP[outSym]?.id || null,
        mengeOut:  parseFloat(menge),
        coinIn:    swapCoinIn.sym,
        coinIdIn:  swapCoinIn.id || COIN_MAP[swapCoinIn.sym]?.id || null,
        mengeIn:   parseFloat(mengeIn),
        preis:     parseFloat(preis),
        gebuehr:   parseFloat(gebuehr) || 0,
        exchange, notiz,
        externalTransfer: false,
      });
    } else {
      const m = isDeriv ? (parseFloat(menge) || 0) : parseFloat(menge);
      // Derivat: preis ist der SIGNIERTE €-PnL (negativ erlaubt) — NICHT auf 0
      // zwingen (sonst Datenverlust beim Bearbeiten importierter Futures-PnL).
      const p = isDeriv
        ? (parseFloat(preis) || 0)
        : (needsPrice ? parseFloat(preis) : (allowsPrice && preis ? parseFloat(preis) : 0));
      // externalTransfer ist nur bei einzahlung/auszahlung relevant.
      // Bei allen anderen Typen (incl. airdrop/verkauf nach 'gift'-Wahl) explizit false.
      const isTransfer = typ === 'einzahlung' || typ === 'auszahlung';
      const externalTransfer = isTransfer ? (sourceMode === 'external') : false;
      const isAcquisition = typ === 'kauf' || INCOME_TYPES.has(typ);
      onSave(
        {
          ...tx, typ, datum, menge: m, preis: p,
          // Coin explizit setzen — bei Tausch→Einzel-Asset-Umklassifizierung war
          // tx.coin null; coin hält den (erhaltenen) Coin. Swap-Felder nullen,
          // sonst blieben coinOut/coinIn/mengeOut/mengeIn als Geister hängen.
          coin, sym: coin,
          ...(isSwap ? { coinId: meta?.id || null, coinOut: null, coinIdOut: null, mengeOut: null, coinIn: null, coinIdIn: null, mengeIn: null } : {}),
          stueck: needsPrice && p ? p / m : 0,
          gebuehr: parseFloat(gebuehr) || 0,
          exchange, notiz, externalTransfer,
          hash: (tx.hash || hash) || null,
          // Ziel-Wallet (MoonPay → Ledger) nur bei Käufen/Zuflüssen; leer → null.
          ...(isAcquisition ? { wallet: wallet || null } : {}),
        }
      );
    }
  };

  // ── FIAT-GELD bearbeiten (Bank ↔ Börse) ──────────────────────────────────
  // Eigener, sauberer Pfad: Richtung (Ein-/Auszahlung) + Datum + Betrag (€) +
  // Börse + Gebühr + Notiz. KEIN Coin-/Quelle-/Tausch-/Verbindungs-Block (Fiat
  // wird nie gepaart). Speichert bestandsneutral als einzahlung/auszahlung mit
  // unverändertem Fiat-Coin; preis = menge (EUR-Wert), damit nichts verloren geht.
  if (isFiatTx) {
    const m = parseFloat(menge) || 0;
    const isEur = String(tx.coin).toUpperCase() === 'EUR';
    const canSaveFiat = !!datum && menge !== '' && m > 0;
    const dirs = [
      { id: 'in',  c: '#4FAEC0', wm: '#A9DBE6', label: t('tw_type_fiat_in') },
      { id: 'out', c: '#D488A8', wm: '#EEC2D9', label: t('tw_type_fiat_out') },
    ];
    const saveFiat = () => onSave({
      ...tx,
      typ: fiatDir === 'out' ? 'auszahlung' : 'einzahlung',
      datum, menge: m,
      preis: isEur ? m : (parseFloat(preis) || 0),
      stueck: 0,
      gebuehr: parseFloat(gebuehr) || 0,
      exchange, notiz,
      externalTransfer: false,
    });
    return (
      <div style={{ padding: isWide ? '16px 0 32px' : '16px 16px 100px', maxWidth: isWide ? 640 : 'none', margin: isWide ? '0 auto' : undefined }}>
        <style>{WIZ_STAGGER_CSS}</style>
        <div style={{ marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <CoinImage sym={tx.coin} marketData={marketData} size={28} />
            <h2 style={{ margin: 0, fontFamily: F.display, fontSize: 22, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.05, color: T.text }}>{tx.coin}</h2>
          </div>
          <div style={{ marginTop: 9, fontSize: 13, color: T.sub, lineHeight: 1.5, fontFamily: F.family }}>
            <span style={{ color: fiatDir === 'out' ? '#D488A8' : '#4FAEC0', fontWeight: 700 }}>{fiatDir === 'out' ? t('tw_type_fiat_out') : t('tw_type_fiat_in')}</span> · {tx.exchange} · {t('tw_edit')}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ ...mkFieldLabel(T), marginBottom: 10 }}>{t('tw_tx_type')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
            {dirs.map(o => {
              const active = fiatDir === o.id;
              return (
                <button key={o.id} onClick={() => setFiatDir(o.id)} className="wizPress wizChip" style={{
                  position: 'relative', overflow: 'hidden',
                  padding: '11px 8px', borderRadius: mr(14), cursor: 'pointer',
                  background: o.c, color: '#fff', border: 'none', fontFamily: F.family,
                  transition: `transform ${DUR.base}ms ${EASE}, opacity ${DUR.fast}ms ease`,
                  transform: active ? 'scale(1.03)' : 'scale(1)', opacity: active ? 1 : 0.55,
                  textAlign: 'left', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
                  minHeight: 72, WebkitTapHighlightColor: 'transparent',
                }}>
                  <svg className="wizChipWm" viewBox="0 0 24 24" fill="none" stroke={o.wm} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ position: 'absolute', right: -10, bottom: -12, width: 56, height: 56, pointerEvents: 'none' }}><path d={FIAT_BANKNOTE} /></svg>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'relative', zIndex: 2 }}><path d={FIAT_BANKNOTE} /></svg>
                  <span style={{ position: 'relative', zIndex: 2, fontFamily: F.brand, fontSize: 11.5, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', lineHeight: 1 }}>{o.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div style={fieldGroupStyle(T)}>
          <FieldRow label={t('date')} type="date" value={datum} onChange={setDatum} />
          <FieldNum label={`${t('amount')} (${tx.coin})`} value={menge} onChange={setMenge} placeholder="0.00" />
          <FieldExchange label={t('tw_exchange')} value={exchange} onChange={(lbl) => setExchange(lbl)} />
          <FieldNum label={`${t('tw_fee')} (${tx.coin})`} value={gebuehr} onChange={setGebuehr} placeholder="0.00" />
          <FieldRow label={t('tw_note_opt')} value={notiz} onChange={setNotiz} placeholder={fiatDir === 'out' ? 'Börse → Bank' : 'Bank → Börse'} />
        </div>

        <button onClick={saveFiat} disabled={!canSaveFiat} style={canSaveFiat ? mkBtnPrimary(T) : mkBtnDisabled(T)}>✓ {t('tw_save_changes')}</button>
        <button onClick={onBack} style={mkBtnGhost(T)}>{t('cancel')}</button>
      </div>
    );
  }

  if (isSwap && editingAsSwap) {
    return (
      <div style={{ padding: '20px 16px 100px' }}>
        <style>{WIZ_STAGGER_CSS}</style>
        <div style={{ marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CoinImage sym={tx.coinOut} marketData={marketData} size={26} />
            <span style={{ color: '#888BFF', fontSize: 16, fontWeight: 700 }}>→</span>
            <CoinImage sym={tx.coinIn} marketData={marketData} size={26} />
            <h2 style={{ margin: '0 0 0 4px', fontFamily: F.display, fontSize: 22, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.05, color: T.text }}>
              {tx.coinOut} → {tx.coinIn}
            </h2>
          </div>
          <div style={{ marginTop: 9, fontSize: 13, color: T.sub, lineHeight: 1.5, fontFamily: F.family }}>
            <span style={{ color: '#888BFF', fontWeight: 700 }}>{t('swap')}</span> · {tx.exchange} · {t('tw_edit_swap')}
          </div>
        </div>

        {/* Typ-Picker — wie bei jedem anderen Typ. Wechsel weg von „Tausch"
            klassifiziert die TX in einen Einzel-Asset-Typ um (erhaltener Coin). */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ ...mkFieldLabel(T), marginBottom: 10 }}>{t('tw_tx_type')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {EDIT_TYPES.map(opt => {
              const active = typ === opt.id;
              return (
                <button key={opt.id} onClick={() => handleTypChange(opt.id)} className="wizPress wizChip" style={{
                  position: 'relative', overflow: 'hidden',
                  padding: '11px 8px', borderRadius: mr(14), cursor: 'pointer',
                  background: opt.chip || opt.color, color: '#fff', border: 'none', fontFamily: F.family,
                  transition: `transform ${DUR.base}ms ${EASE}, opacity ${DUR.fast}ms ease`,
                  transform: active ? 'scale(1.03)' : 'scale(1)', opacity: active ? 1 : 0.55,
                  textAlign: 'left', display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                  justifyContent: 'space-between', gap: 12, minHeight: 72, WebkitTapHighlightColor: 'transparent',
                }}>
                  {opt.iconPath && (
                    <svg className="wizChipWm" viewBox="0 0 24 24" fill="none" stroke={opt.wm || '#ffffff'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                      style={{ position: 'absolute', right: -10, bottom: -12, width: 56, height: 56, pointerEvents: 'none' }}><path d={opt.iconPath} /></svg>
                  )}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'relative', zIndex: 2 }}><path d={opt.iconPath} /></svg>
                  <span style={{ position: 'relative', zIndex: 2, fontFamily: F.brand, fontSize: 11.5, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', lineHeight: 1 }}>{t(opt.labelKey)}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div style={fieldGroupStyle(T)}>
          <FieldRow label={t('date')} type="date" value={datum} onChange={setDatum} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0 }}>
            <FieldNum label={`${t('tw_swap_amount_out')} (${tx.coinOut})`} value={mengeOut} onChange={setMengeOut} placeholder="0.00" />
            <FieldNum label={`${t('tw_swap_amount_in')} (${tx.coinIn})`} value={mengeIn} onChange={setMengeIn} placeholder="0.00" />
          </div>
          <div style={mkFieldBase(T)}>
            <div style={mkFieldLabel(T)}>{t('value')} ({symbol})</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="text" inputMode="decimal"
                value={preis} onChange={e => setPreis(e.target.value)}
                placeholder="0.00"
                style={{ ...mkFieldInput(T), flex: 1, minWidth: 0 }}
              />
              <RateButton onClick={fetchHistoricalPrice} disabled={!mengeOut || !datum || fetchingPrice} fetching={fetchingPrice} />
            </div>
          </div>
          {priceErr && (
            <div style={{ fontSize: 12, color: T.sub, marginTop: -4, marginBottom: 8, lineHeight: 1.45 }}>
              {t('tw_hist_price_missing')}
            </div>
          )}
          <FeeInfoBanner tx={tx} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0 }}>
            <FieldNum label={`${t('tw_fee')} (${symbol})`} value={gebuehr} onChange={setGebuehr} placeholder="0.00" />
            <FieldSelect label={t('tw_exchange')} value={exchange} onChange={setExchange} options={EXCHANGES} withExchangeLogo />
          </div>
          <FieldRow label={t('tw_note_opt')} value={notiz} onChange={setNotiz} placeholder={t('tw_note_ph_uniswap')} />
        </div>

        <button onClick={handleSave} disabled={!canSave} style={canSave ? mkBtnPrimary(T) : mkBtnDisabled(T)}>✓ {t('tw_save_changes')}</button>
        <button onClick={onBack} style={mkBtnGhost(T)}>{t('cancel')}</button>
      </div>
    );
  }

  return (
    <div style={{
      padding: isWide ? '16px 0 32px' : '16px 16px 100px',
      maxWidth: isWide ? 640 : 'none',
      margin: isWide ? '0 auto' : undefined,
    }}>
      <style>{WIZ_STAGGER_CSS}</style>
      <div style={{ marginBottom: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <CoinImage sym={coin || tx.coin} marketData={marketData} size={28} />
          <h2 style={{ margin: 0, fontFamily: F.display, fontSize: 22, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.05, color: T.text }}>
            {meta?.name || coin || tx.coin}
          </h2>
          <div style={{ flex: 1 }} />
          {mData?.price && (
            <span style={{ fontSize: 12.5, fontWeight: 600, color: T.sub, fontFamily: F.family, flexShrink: 0 }}>
              {fmt(convert(mData.price), locale)} {symbol}
            </span>
          )}
        </div>
        <div style={{ marginTop: 9, fontSize: 13, color: T.sub, lineHeight: 1.5, fontFamily: F.family }}>
          <span style={{ color: typMeta.color, fontWeight: 700 }}>{t(typMeta.labelKey)}</span> · {tx.exchange} · {t('tw_edit')}
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ ...mkFieldLabel(T), marginBottom: 10 }}>{t('tw_tx_type')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
          {EDIT_TYPES.map(opt => {
            const active = typ === opt.id;
            return (
              <button key={opt.id} onClick={() => handleTypChange(opt.id)} className="wizPress wizChip" style={{
                position: 'relative', overflow: 'hidden',
                padding: '11px 8px', borderRadius: mr(14), cursor: 'pointer',
                background: opt.chip || opt.color, color: '#fff', border: 'none',
                fontFamily: F.family,
                transition: `transform ${DUR.base}ms ${EASE}, opacity ${DUR.fast}ms ease`,
                transform: active ? 'scale(1.03)' : 'scale(1)',
                opacity: active ? 1 : 0.55,
                textAlign: 'left',
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
                minHeight: 72, WebkitTapHighlightColor: 'transparent',
              }}>
                {/* helles Wasserzeichen, von der Ecke beschnitten */}
                {opt.iconPath && (
                  <svg className="wizChipWm" viewBox="0 0 24 24" fill="none" stroke={opt.wm || '#ffffff'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ position: 'absolute', right: -10, bottom: -12, width: 56, height: 56, pointerEvents: 'none' }}>
                    <path d={opt.iconPath} />
                  </svg>
                )}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'relative', zIndex: 2 }}>
                  <path d={opt.iconPath} />
                </svg>
                <span style={{
                  position: 'relative', zIndex: 2,
                  fontFamily: F.brand, fontSize: 11.5, fontWeight: 800,
                  color: '#fff', letterSpacing: '-0.02em', lineHeight: 1,
                }}>{t(opt.labelKey)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Transfer-Klassifizierung — nur bei einzahlung/auszahlung */}
      {(typ === 'einzahlung' || typ === 'auszahlung') && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ ...mkFieldLabel(T), marginBottom: 10 }}>{t('tw_what_transfer')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {[
              { id: 'own',      chip: '#74A8DB', wm: '#A6CBEE', glyph: PICK_GLYPHS.wallet, label: t('tw_src_own_short') },
              { id: 'external', chip: '#E0976A', wm: '#F0BD98', glyph: PICK_GLYPHS.person, label: t('tw_src_ext_short') },
              { id: 'gift',     chip: '#A78FD9', wm: '#CAB8ED', glyph: PICK_GLYPHS.gift,   label: t('tw_gift') },
            ].map(opt => {
              const active = sourceMode === opt.id;
              return (
                <button key={opt.id} onClick={() => handleSourceModeChange(opt.id)} className="wizPress wizChip" style={{
                  position: 'relative', overflow: 'hidden',
                  padding: '11px 8px', borderRadius: mr(14), cursor: 'pointer',
                  background: opt.chip, color: '#fff', border: 'none', fontFamily: F.family,
                  transition: `transform ${DUR.base}ms ${EASE}, opacity ${DUR.fast}ms ease`,
                  transform: active ? 'scale(1.03)' : 'scale(1)',
                  opacity: active ? 1 : 0.55,
                  textAlign: 'left',
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
                  minHeight: 72, WebkitTapHighlightColor: 'transparent',
                }}>
                  <svg className="wizChipWm" viewBox="0 0 24 24" fill="none" stroke={opt.wm} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ position: 'absolute', right: -10, bottom: -12, width: 56, height: 56, pointerEvents: 'none' }}>{opt.glyph}</svg>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'relative', zIndex: 2 }}>{opt.glyph}</svg>
                  <span style={{ position: 'relative', zIndex: 2, fontFamily: F.brand, fontSize: 11.5, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', lineHeight: 1 }}>{opt.label}</span>
                </button>
              );
            })}
          </div>
          {sourceMode === 'external' && (
            <div style={{
              marginTop: 10, padding: '8px 12px',
              background: withAlpha('#F59E0B', 0.08),
              border: `1px solid ${withAlpha('#F59E0B', 0.22)}`,
              borderRadius: mr(10),
              fontSize: 11.5, color: T.sub, fontFamily: F.family, lineHeight: 1.45,
            }}>
              {t('tw_external_banner')}
            </div>
          )}
        </div>
      )}

      {/* Verbindung — verbinden / trennen / Gegenbuchung anlegen.
          Nur bei eigene-Wallet-Transfers (own); extern/Geschenk haben kein Gegenstück. */}
      {(typ === 'einzahlung' || typ === 'auszahlung') && sourceMode === 'own' && onLink && (
        <TransferConnectionSection
          tx={tx}
          txs={txs}
          marketData={marketData}
          onLink={onLink}
          onUnlink={onUnlink}
          onCreateCounterpart={onCreateCounterpart}
        />
      )}

      <div style={fieldGroupStyle(T)}>
        <FieldRow label={t('date')} type="date" value={datum} onChange={setDatum} />
        {/* Derivat (Futures PnL / Funding): keine Coin-Menge — nur der €-PnL zählt. */}
        {!isDeriv && (
          <FieldNum
            label={`${editingAsSwap ? t('tw_swap_amount_out') : t('amount')} (${coin || tx.coin || ''})`}
            value={menge} onChange={setMenge} placeholder="0.00"
          />
        )}

        {/* Tausch-Umwandlung: erhaltener Coin + Menge + Wert */}
        {editingAsSwap && (
          <>
            <InlineCoinPicker label={t('tw_swap_coin_in')} selected={swapCoinIn} onSelect={setSwapCoinIn} marketData={marketData} />
            <FieldNum
              label={`${t('tw_swap_amount_in')}${swapCoinIn ? ` (${swapCoinIn.sym})` : ''}`}
              value={mengeIn} onChange={setMengeIn} placeholder="0.00"
            />
            <FieldNum label={`${t('value')} (${symbol})`} value={preis} onChange={setPreis} placeholder="0.00" />
          </>
        )}

        {needsPrice && (
          <div style={mkFieldBase(T)}>
            <div style={mkFieldLabel(T)}>{t('tw_total')} ({symbol})</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="text" inputMode="decimal"
                value={preis} onChange={e => setPreis(e.target.value)}
                placeholder="0.00"
                style={{ ...mkFieldInput(T), flex: 1, minWidth: 0 }}
              />
              <RateButton onClick={fetchHistoricalPrice} disabled={!menge || !datum || fetchingPrice} fetching={fetchingPrice} />
            </div>
          </div>
        )}

        {allowsPrice && !needsPrice && (
          <FieldNum label={t('tw_value_at_time')} value={preis} onChange={setPreis} placeholder="0.00" />
        )}

        {isDeriv && (
          <div style={mkFieldBase(T)}>
            <div style={mkFieldLabel(T)}>{t('tw_pnl_label')} ({symbol})</div>
            <input
              type="text" inputMode="decimal"
              value={preis} onChange={e => setPreis(e.target.value)}
              placeholder={t('tw_pnl_ph')}
              style={{ ...mkFieldInput(T), width: '100%' }}
            />
            <div style={{ fontSize: 11, color: T.sub, marginTop: 6, fontFamily: F.family }}>
              {t('tw_pnl_hint')}
            </div>
          </div>
        )}

        {tx.countervalue > 0 && typ === 'einzahlung' && (
          <InfoBanner
            tone="success"
            title={t('tw_fiat_buy_q')}
            cta={`→ ${t('tw_switch')}`}
            onClick={() => handleTypChange('kauf')}
          >
            {t('tw_countervalue', { v: `${fmt(convert(tx.countervalue), locale)} ${symbol}` })}
          </InfoBanner>
        )}

        {/* Stückpreis (abgeleitet aus Gesamt ÷ Menge) — neutrales Feld wie die
            anderen, KEIN grün-getönter Neon-Container (feedback_no_pills_glow).
            Der Wert ist die Bühne: groß, mono, dezenter „/ Coin"-Suffix. */}
        {stueck && (
          <div style={mkFieldBase(T)}>
            <div style={mkFieldLabel(T)}>{t('tw_unit_price')}</div>
            <div style={{
              fontFamily: F.display, fontVariantNumeric: 'tabular-nums',
              fontSize: 17, fontWeight: 700, color: T.text, letterSpacing: '-0.02em',
              marginTop: 2,
            }}>
              {fmt(convert(parseFloat(stueck)), locale)} {symbol}
              <span style={{ color: T.sub, fontSize: 13, fontWeight: 600 }}> / {tx.coin}</span>
            </div>
          </div>
        )}

        <FeeInfoBanner tx={tx} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0 }}>
          {/* „enthalten" grenzt die im Trade eingebettete Gebühr von einer
              eigenständigen Gebühr-Buchung (Gebühren-Reiter) ab. */}
          <FieldNum label={`${t('tw_fee_incl')} (${symbol})`} value={gebuehr} onChange={setGebuehr} placeholder="0.00" />
          {/* Nur bei Kauf: Schritt-0 (direkt vs. Dienstleister). Dienstleister-Pfad
              setzt exchange=Dienstleister + wallet=Ziel → Anzeige „MoonPay → Ledger".
              wallet wird nur im Kauf-Kontext verändert (sonst tx.wallet unangetastet). */}
          <FieldSelect
            label={t('tw_exchange_wallet')}
            value={exchange}
            onChange={(ex, wal) => { setExchange(ex); if (typ === 'kauf') setWallet(wal || ''); }}
            options={EXCHANGES}
            withExchangeLogo
            allowProvider={typ === 'kauf'}
            wallet={wallet}
          />
        </div>

        <FieldRow label={t('tw_note_opt')} value={notiz} onChange={setNotiz} placeholder={t('tw_note_ph_dca_airdrop')} />

        {/* TX-Hash — immer sichtbar (Konsistenz Wallet-/Asset-Edit). Vorhandener
            Hash bleibt gesperrt + Kopier-Button (kein roter Glow mehr); fehlt
            einer, ist das Feld editierbar zum Nachtragen. */}
        <div style={mkFieldBase(T)}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <div style={mkFieldLabel(T)}>{t('tw_txhash')}</div>
            {tx.hash && (
              <span style={{
                fontSize: 10, fontWeight: 700, color: T.mute, fontFamily: F.family,
                letterSpacing: '0.04em',
              }}>{t('tw_locked')}</span>
            )}
          </div>
          {tx.hash ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="text" value={tx.hash} disabled style={{
                background: 'none', border: 'none', outline: 'none',
                color: T.dim, fontFamily: F.family, fontSize: 12,
                flex: 1, minWidth: 0, textOverflow: 'ellipsis', cursor: 'not-allowed',
              }} />
              <button
                onClick={copyHash}
                title={t('tw_copy')}
                aria-label={t('tw_copy')}
                style={{
                  flexShrink: 0, width: 30, height: 30, borderRadius: mr(8),
                  background: 'none', border: 'none', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  color: copied ? T.green : T.sub,
                  WebkitTapHighlightColor: 'transparent',
                  transition: `color ${DUR.fast}ms ${EASE}`,
                }}
              >
                <Icon d={copied ? Icons.check : Icons.copy} size={15} stroke="currentColor" sw={2.4} />
              </button>
            </div>
          ) : (
            <input
              type="text" value={hash} onChange={e => setHash(e.target.value)}
              placeholder="0x…"
              style={{ ...mkFieldInput(T), width: '100%' }}
            />
          )}
        </div>
      </div>

      <button onClick={handleSave} disabled={!canSave} style={canSave ? mkBtnPrimary(T) : mkBtnDisabled(T)}>
        ✓ {t('tw_save_changes')}
      </button>
      <button onClick={onBack} style={mkBtnGhost(T)}>{t('cancel')}</button>
    </div>
  );
}
